import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { FikoVektor3D } from '../../FikoVektor3d';
import { CetvelGeometryService } from './cetvel-geometry.service';
import { yuzeyTipiTespit, miCember } from '../../FikoYT-Fonksiyonlar';
import { gpuOlcumMotoru } from '../gpu/GPUOlcumMotoru';
import { GeometryAnalysisCache, CachedGeometryAnalysis } from '../GeometryAnalysisCache';

@Injectable({
  providedIn: 'root'
})
export class CetvelMathService {
  // 🚀 PRE-ALLOCATED BUFFERS (zero-allocation loops)
  private static readonly _tempVec1 = new THREE.Vector3();
  private static readonly _tempVec2 = new THREE.Vector3();
  private static readonly _tempVec3 = new THREE.Vector3();
  private static readonly _tempAxis = new THREE.Vector3();
  private static readonly _tempCentroid = new THREE.Vector3();
  private static readonly _tempCross = new THREE.Vector3();
  private static readonly _tempRel = new THREE.Vector3();
  private static readonly _tempUVec = new THREE.Vector3();
  private static readonly _tempVVec = new THREE.Vector3();

  // 🚀 ULTRA PERF: Geometry Analysis Cache (avoid recalculating circle/cylinder detection)
  // Cache size: 2000 geometries = enough for most CAD assemblies
  private geometryCache = new GeometryAnalysisCache(2000);

  // 🚀 ASYNC: Calculation queue and cancellation support
  private activeCalculations = new Map<string, AbortController>();
  private calculationQueue: Array<{ id: string; promise: Promise<any> }> = [];

  constructor(private geomService: CetvelGeometryService) { }

  /**
   * 🚀 ASYNC HELPER: Yield to browser (prevent UI freeze)
   * Kullanım: await this.yieldToUI();
   */
  private yieldToUI(): Promise<void> {
    return new Promise(resolve => {
      // setTimeout(0) ile browser event loop'a yield et
      // Bu sayede UI updates, user interactions vs. işlenebilir
      setTimeout(resolve, 0);
    });
  }

  /**
   * 🚀 ASYNC HELPER: Cancel previous calculation if new one starts
   */
  private cancelPreviousCalculation(calculationId: string): void {
    const existing = this.activeCalculations.get(calculationId);
    if (existing) {
      existing.abort(); // Abort signal gönder
      this.activeCalculations.delete(calculationId);
    }
  }

  /**
   * 🚀 ASYNC HELPER: Check if calculation was cancelled
   */
  private isCalculationCancelled(signal: AbortSignal): boolean {
    return signal.aborted;
  }

  /**
   * 🔵 Çizgi geometrisinin çember/yay olup olmadığını tespit et ve merkez+yarıçap döndür
   * Public yapıldı - cetvel component'tan erişim için
   * 🎯 KUMPAS İLE AYNI ALGORİTMA: miCember fonksiyonu kullanılıyor
   */
  public cizgiCemberTespit(cizgiGeom: THREE.BufferGeometry): { merkez: THREE.Vector3, yaricap: number } | null {
    try {
      // 🎯 KUMPAS İLE AYNI: miCember fonksiyonunu kullan (WASM destekli, daha robust)
      const miCem = miCember(cizgiGeom);

      if (miCem && miCem.yaricap && !isNaN(miCem.yaricap)) {
        // Kumpas'taki gibi sapma kontrolü yap
        const avgDeviation = miCem.ortalamaSapma ?? 0;
        const deviationRatio = (avgDeviation / miCem.yaricap) * 100;

        // Gevşek kontrol: %50'den az sapma ve yarıçap 0.1'den büyükse kabul et
        if (deviationRatio <= 50 && miCem.yaricap >= 0.1) {
          return {
            merkez: new THREE.Vector3(miCem.merkez.x, miCem.merkez.y, miCem.merkez.z),
            yaricap: miCem.yaricap
          };
        }
      }
    } catch (e) {
      // Hata durumunda sessizce null döndür
    }
    return null;
  }

  /**
   * 🚀 ASYNC: Yüzey silindir tespiti - ASYNC VERSION (UI dondurmaz!)
   * Public - cetvel component'tan async await ile çağrılır
   * Kullanım: const result = await cetvelMath.yuzeySilindirTespitAsync(geometry);
   */
  public async yuzeySilindirTespitAsync(
    yuzeyGeom: THREE.BufferGeometry,
    calculationId?: string
  ): Promise<{ merkez: THREE.Vector3, yaricap: number, tip: string } | null> {
    // 🚀 CACHE CHECK: Cache hit ise instant return (no async needed)
    const cached = this.geometryCache.get(yuzeyGeom);
    if (cached && cached.circleCenter && cached.avgRadius) {
      return {
        merkez: new THREE.Vector3(cached.circleCenter.x, cached.circleCenter.y, cached.circleCenter.z),
        yaricap: cached.avgRadius,
        tip: cached.surfaceType
      };
    }

    // 🚀 CANCELLATION: Cancel previous calculation if user changed selection
    const id = calculationId || yuzeyGeom.uuid;
    this.cancelPreviousCalculation(id);

    // Create abort controller for this calculation
    const abortController = new AbortController();
    this.activeCalculations.set(id, abortController);

    try {
      // 🚀 ASYNC: Call sync version in chunks with yields
      const result = await this.yuzeySilindirTespitAsyncImpl(yuzeyGeom, abortController.signal);

      // Success - remove from active calculations
      this.activeCalculations.delete(id);
      return result;
    } catch (error: any) {
      // Cancelled or error
      this.activeCalculations.delete(id);
      if (error.name === 'AbortError') {
        return null; // Calculation was cancelled
      }
      throw error;
    }
  }

  /**
   * 🚀 ASYNC IMPLEMENTATION: Silindir tespiti with yields (prevents UI freeze)
   */
  private async yuzeySilindirTespitAsyncImpl(
    yuzeyGeom: THREE.BufferGeometry,
    signal: AbortSignal
  ): Promise<{ merkez: THREE.Vector3, yaricap: number, tip: string } | null> {
    try {
      // Check cancellation
      if (this.isCalculationCancelled(signal)) return null;

      // 🚀 CACHE CHECK: Return immediately if cached
      const cached = this.geometryCache.get(yuzeyGeom);
      if (cached && cached.circleCenter && cached.avgRadius) {
        return {
          merkez: new THREE.Vector3(cached.circleCenter.x, cached.circleCenter.y, cached.circleCenter.z),
          yaricap: cached.avgRadius,
          tip: cached.surfaceType
        };
      }

      const yuzeyTip = yuzeyTipiTespit(yuzeyGeom);
      if (yuzeyTip && yuzeyTip.icerik) {
        const tipi = yuzeyTip.icerik.tipi;
        if ((tipi === 'silindir' || tipi === 'kure' || tipi === 'egri') && yuzeyTip.icerik.avgRadius > 0) {
          const posAttr = yuzeyGeom.attributes.position;
          const normalAttr = yuzeyGeom.attributes.normal;

          if (!posAttr || posAttr.count < 6) {
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          const vertexCount = posAttr.count;
          const posArray = posAttr.array as Float32Array;
          const normalArray = normalAttr ? normalAttr.array as Float32Array : null;

          // ADIM 1: Silindir eksenini bul (with yield)
          const axis = CetvelMathService._tempAxis.set(0, 0, 1);
          let axisFound = false;

          if (normalArray && vertexCount >= 2) {
            const bestCross = CetvelMathService._tempVec1.set(0, 0, 0);
            const n1 = CetvelMathService._tempVec2;
            const n2 = CetvelMathService._tempVec3;
            const cross = CetvelMathService._tempCross;
            let maxCrossLen = 0;

            const limit = Math.min(vertexCount, vertexCount > 1000 ? 20 : 30);
            const earlyExitThreshold = 0.95;

            // 🚀 YIELD: Nested loop ağır olabilir, her 5 dış iterasyonda yield
            for (let i = 0; i < limit; i++) {
              if (this.isCalculationCancelled(signal)) return null;
              if (i > 0 && i % 5 === 0) await this.yieldToUI(); // Yield every 5 iterations

              const i3 = i * 3;
              n1.set(normalArray[i3], normalArray[i3 + 1], normalArray[i3 + 2]).normalize();
              for (let j = i + 1; j < limit; j++) {
                const j3 = j * 3;
                n2.set(normalArray[j3], normalArray[j3 + 1], normalArray[j3 + 2]).normalize();
                cross.crossVectors(n1, n2);
                const len = cross.length();
                if (len > maxCrossLen) {
                  maxCrossLen = len;
                  bestCross.copy(cross);

                  if (len >= earlyExitThreshold) {
                    axisFound = true;
                    axis.copy(bestCross).normalize();
                    break;
                  }
                }
              }
              if (axisFound) break;
            }

            if (maxCrossLen > 0.01) {
              axis.copy(bestCross).normalize();
              axisFound = true;
            }
          }

          // ADIM 2: PCA fallback (with yield)
          if (!axisFound) {
            if (this.isCalculationCancelled(signal)) return null;
            await this.yieldToUI(); // Yield before PCA

            const centroidTemp = CetvelMathService._tempCentroid.set(0, 0, 0);
            for (let i = 0; i < vertexCount; i++) {
              const i3 = i * 3;
              centroidTemp.x += posArray[i3];
              centroidTemp.y += posArray[i3 + 1];
              centroidTemp.z += posArray[i3 + 2];
            }
            centroidTemp.divideScalar(vertexCount);

            let cxx = 0, cyy = 0, czz = 0, cxy = 0, cxz = 0, cyz = 0;
            for (let i = 0; i < vertexCount; i++) {
              const i3 = i * 3;
              const dx = posArray[i3] - centroidTemp.x;
              const dy = posArray[i3 + 1] - centroidTemp.y;
              const dz = posArray[i3 + 2] - centroidTemp.z;
              cxx += dx * dx; cyy += dy * dy; czz += dz * dz;
              cxy += dx * dy; cxz += dx * dz; cyz += dy * dz;
            }

            const newAxis = CetvelMathService._tempVec1;
            const maxPCAIterations = 15;
            const convergenceThreshold = 1e-6;

            // 🚀 YIELD: PCA iterasyonları (her 3 iterasyonda yield)
            for (let iter = 0; iter < maxPCAIterations; iter++) {
              if (this.isCalculationCancelled(signal)) return null;
              if (iter > 0 && iter % 3 === 0) await this.yieldToUI();

              newAxis.set(
                cxx * axis.x + cxy * axis.y + cxz * axis.z,
                cxy * axis.x + cyy * axis.y + cyz * axis.z,
                cxz * axis.x + cyz * axis.y + czz * axis.z
              );
              if (newAxis.length() > 0.001) {
                newAxis.normalize();
                const axisDiff = Math.abs(newAxis.x - axis.x) + Math.abs(newAxis.y - axis.y) + Math.abs(newAxis.z - axis.z);
                axis.copy(newAxis);
                if (axisDiff < convergenceThreshold) break;
              }
            }
          }

          // ADIM 3: Basis vektörleri
          const uVec = CetvelMathService._tempUVec;
          const vVec = CetvelMathService._tempVVec;
          if (Math.abs(axis.x) < 0.9) {
            uVec.set(1, 0, 0);
            uVec.crossVectors(axis, uVec).normalize();
          } else {
            uVec.set(0, 1, 0);
            uVec.crossVectors(axis, uVec).normalize();
          }
          vVec.crossVectors(axis, uVec).normalize();

          // ADIM 4: 2D projeksiyon (with yield)
          if (this.isCalculationCancelled(signal)) return null;
          await this.yieldToUI(); // Yield before projection

          const centroid = CetvelMathService._tempCentroid.set(0, 0, 0);
          for (let i = 0; i < vertexCount; i++) {
            const i3 = i * 3;
            centroid.x += posArray[i3];
            centroid.y += posArray[i3 + 1];
            centroid.z += posArray[i3 + 2];
          }
          centroid.divideScalar(vertexCount);

          const projected2D: { x: number; y: number }[] = [];
          const rel = CetvelMathService._tempRel;

          // 🚀 YIELD: Projection loop (her 500 vertex'te yield)
          for (let i = 0; i < vertexCount; i++) {
            if (i > 0 && i % 500 === 0) {
              if (this.isCalculationCancelled(signal)) return null;
              await this.yieldToUI();
            }
            const i3 = i * 3;
            rel.set(posArray[i3] - centroid.x, posArray[i3 + 1] - centroid.y, posArray[i3 + 2] - centroid.z);
            const axisProj = rel.dot(axis);
            rel.x -= axis.x * axisProj;
            rel.y -= axis.y * axisProj;
            rel.z -= axis.z * axisProj;
            projected2D.push({ x: rel.dot(uVec), y: rel.dot(vVec) });
          }

          // ADIM 5: Circle fitting
          let sumX = 0, sumY = 0, sumX2 = 0, sumY2 = 0, sumXY = 0;
          let sumX3 = 0, sumY3 = 0, sumX2Y = 0, sumXY2 = 0;

          projected2D.forEach(p => {
            sumX += p.x; sumY += p.y;
            sumX2 += p.x * p.x; sumY2 += p.y * p.y;
            sumXY += p.x * p.y;
            sumX3 += p.x ** 3; sumY3 += p.y ** 3;
            sumX2Y += p.x * p.x * p.y; sumXY2 += p.x * p.y * p.y;
          });

          const N = projected2D.length;
          const A = N * sumX2 - sumX * sumX;
          const B = N * sumXY - sumX * sumY;
          const C = N * sumY2 - sumY * sumY;
          const D = 0.5 * (N * sumX3 + N * sumXY2 - sumX * sumX2 - sumX * sumY2);
          const E = 0.5 * (N * sumX2Y + N * sumY3 - sumY * sumX2 - sumY * sumY2);

          const denom = A * C - B * B;
          if (Math.abs(denom) < 1e-10) {
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          let cx2d = (D * C - B * E) / denom;
          let cy2d = (A * E - B * D) / denom;

          // ADIM 6: Gauss-Newton refinement (with yield)
          const maxGNIterations = 6;
          const gnConvergenceThreshold = 1e-8;

          // 🚀 YIELD: Gauss-Newton iterations (her 2 iterasyonda yield)
          for (let iter = 0; iter < maxGNIterations; iter++) {
            if (this.isCalculationCancelled(signal)) return null;
            if (iter > 0 && iter % 2 === 0) await this.yieldToUI();

            let rSum = 0;
            for (let i = 0; i < projected2D.length; i++) {
              const p = projected2D[i];
              rSum += Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
            }
            const rMean = rSum / N;

            let gradX = 0, gradY = 0;
            for (let i = 0; i < projected2D.length; i++) {
              const p = projected2D[i];
              const dx = p.x - cx2d;
              const dy = p.y - cy2d;
              const ri = Math.sqrt(dx * dx + dy * dy);
              if (ri > 0.001) {
                const err = ri - rMean;
                gradX += err * (-dx / ri);
                gradY += err * (-dy / ri);
              }
            }

            const step = 0.5 / N;
            const newCx = cx2d - step * gradX;
            const newCy = cy2d - step * gradY;

            const centerShift = Math.abs(newCx - cx2d) + Math.abs(newCy - cy2d);
            cx2d = newCx;
            cy2d = newCy;

            if (centerShift < gnConvergenceThreshold) break;
          }

          // Final yarıçap
          const allRadii: number[] = [];
          projected2D.forEach(p => {
            const r = Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
            allRadii.push(r);
          });
          allRadii.sort((a, b) => a - b);
          const medianIndex = Math.floor(allRadii.length / 2);
          const gercekYaricap = allRadii[medianIndex];

          // 3D merkez
          const gercekMerkez = new THREE.Vector3()
            .copy(centroid)
            .addScaledVector(uVec, cx2d)
            .addScaledVector(vVec, cy2d);

          if (isNaN(gercekYaricap) || gercekYaricap < 0.1 || gercekYaricap > 100000) {
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          // 🚀 CACHE STORE
          const cacheEntry: CachedGeometryAnalysis = {
            surfaceType: tipi as any,
            accuracy: 95,
            avgRadius: gercekYaricap,
            minRadius: gercekYaricap * 0.95,
            maxRadius: gercekYaricap * 1.05,
            circleCenter: new FikoVektor3D(gercekMerkez.x, gercekMerkez.y, gercekMerkez.z),
            circleRMS: 0,
            axis: new FikoVektor3D(axis.x, axis.y, axis.z),
            vertexCount: vertexCount,
            hashedBounds: '',
            timestamp: Date.now()
          };
          this.geometryCache.set(yuzeyGeom, cacheEntry);

          return {
            merkez: gercekMerkez,
            yaricap: gercekYaricap,
            tip: tipi
          };
        }
      }
    } catch (e) {
      // Silent fail
    }
    return null;
  }

  /**
   * 🔵 Yüzey geometrisinin silindir/küre olup olmadığını tespit et ve merkez+yarıçap döndür
   * Public yapıldı - cetvel component'tan erişim için
   * 🚀 ULTRA PERF: Cache-aware - aynı geometry için tekrar hesaplama yapmaz
   * ⚠️ SYNC VERSION: UI freeze edebilir (cache miss durumunda). Async version kullan: yuzeySilindirTespitAsync()
   */
  public yuzeySilindirTespit(yuzeyGeom: THREE.BufferGeometry): { merkez: THREE.Vector3, yaricap: number, tip: string } | null {
    try {
      // 🚀 CACHE CHECK: Daha önce bu geometry analiz edildiyse sonucu döndür
      const cached = this.geometryCache.get(yuzeyGeom);
      if (cached && cached.circleCenter && cached.avgRadius) {
        // Cache hit! 30-50ms hesaplama → <0.1ms (300-500x hızlanma!)
        return {
          merkez: new THREE.Vector3(cached.circleCenter.x, cached.circleCenter.y, cached.circleCenter.z),
          yaricap: cached.avgRadius,
          tip: cached.surfaceType
        };
      }

      const yuzeyTip = yuzeyTipiTespit(yuzeyGeom);
      if (yuzeyTip && yuzeyTip.icerik) {
        const tipi = yuzeyTip.icerik.tipi;
        // 🎯 DÜZELTME: 'egri' tipi de radiüslü yüzey olarak kabul edilmeli
        if ((tipi === 'silindir' || tipi === 'kure' || tipi === 'egri') && yuzeyTip.icerik.avgRadius > 0) {
          // 🎯 KUMPAS ALGORİTMASI: Doğru merkez hesaplama (2D Circle Fitting)
          const posAttr = yuzeyGeom.attributes.position;
          const normalAttr = yuzeyGeom.attributes.normal;

          if (!posAttr || posAttr.count < 6) {
            // Fallback: ağırlık merkezi
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          // ADIM 1: Tüm vertex ve normalleri topla - 🚀 ZERO-ALLOCATION: Float32Array kullan
          const vertexCount = posAttr.count;
          const posArray = posAttr.array as Float32Array;
          const normalArray = normalAttr ? normalAttr.array as Float32Array : null;

          // ADIM 2: Silindir eksenini bul (Normal cross product) - 🚀 ZERO-ALLOCATION
          const axis = CetvelMathService._tempAxis.set(0, 0, 1);
          let axisFound = false;

          if (normalArray && vertexCount >= 2) {
            const bestCross = CetvelMathService._tempVec1.set(0, 0, 0);
            const n1 = CetvelMathService._tempVec2;
            const n2 = CetvelMathService._tempVec3;
            const cross = CetvelMathService._tempCross;
            let maxCrossLen = 0;

            // 🚀 ULTRA PERF: Limit sample size (50 → 30) ve early exit ekle
            // Adaptive sampling: büyük yüzeyler için daha az sample
            const limit = Math.min(vertexCount, vertexCount > 1000 ? 20 : 30);
            const earlyExitThreshold = 0.95; // %95 perpendicular = yeterince iyi

            for (let i = 0; i < limit; i++) {
              const i3 = i * 3;
              n1.set(normalArray[i3], normalArray[i3 + 1], normalArray[i3 + 2]).normalize();
              for (let j = i + 1; j < limit; j++) {
                const j3 = j * 3;
                n2.set(normalArray[j3], normalArray[j3 + 1], normalArray[j3 + 2]).normalize();
                cross.crossVectors(n1, n2);
                const len = cross.length();
                if (len > maxCrossLen) {
                  maxCrossLen = len;
                  bestCross.copy(cross);

                  // 🚀 EARLY EXIT: Neredeyse dik normaller bulduysak, yeterli!
                  if (len >= earlyExitThreshold) {
                    axisFound = true;
                    axis.copy(bestCross).normalize();
                    break; // İç loop'tan çık
                  }
                }
              }
              if (axisFound) break; // Dış loop'tan da çık
            }

            if (maxCrossLen > 0.01) {
              axis.copy(bestCross).normalize();
              axisFound = true;
            }
          }

          // PCA fallback - 🚀 ZERO-ALLOCATION: Float32Array kullan
          if (!axisFound) {
            const centroidTemp = CetvelMathService._tempCentroid.set(0, 0, 0);
            for (let i = 0; i < vertexCount; i++) {
              const i3 = i * 3;
              centroidTemp.x += posArray[i3];
              centroidTemp.y += posArray[i3 + 1];
              centroidTemp.z += posArray[i3 + 2];
            }
            centroidTemp.divideScalar(vertexCount);

            let cxx = 0, cyy = 0, czz = 0, cxy = 0, cxz = 0, cyz = 0;
            for (let i = 0; i < vertexCount; i++) {
              const i3 = i * 3;
              const dx = posArray[i3] - centroidTemp.x;
              const dy = posArray[i3 + 1] - centroidTemp.y;
              const dz = posArray[i3 + 2] - centroidTemp.z;
              cxx += dx * dx; cyy += dy * dy; czz += dz * dz;
              cxy += dx * dy; cxz += dx * dz; cyz += dy * dz;
            }

            const newAxis = CetvelMathService._tempVec1;
            // 🚀 ULTRA PERF: PCA iterasyonu (30 → 15) + convergence check (early exit)
            const maxPCAIterations = 15; // 30'dan 15'e düşürdük (yeterince doğru)
            const convergenceThreshold = 1e-6; // Convergence kontrolü

            for (let iter = 0; iter < maxPCAIterations; iter++) {
              newAxis.set(
                cxx * axis.x + cxy * axis.y + cxz * axis.z,
                cxy * axis.x + cyy * axis.y + cyz * axis.z,
                cxz * axis.x + cyz * axis.y + czz * axis.z
              );
              if (newAxis.length() > 0.001) {
                newAxis.normalize();

                // 🚀 EARLY EXIT: Axis converged? (değişim çok küçükse bitir)
                const axisDiff = Math.abs(newAxis.x - axis.x) + Math.abs(newAxis.y - axis.y) + Math.abs(newAxis.z - axis.z);
                axis.copy(newAxis);

                if (axisDiff < convergenceThreshold) {
                  break; // Converged! Daha fazla iterasyona gerek yok
                }
              }
            }
          }

          // ADIM 3: Basis vektörleri - 🚀 ZERO-ALLOCATION
          const uVec = CetvelMathService._tempUVec;
          const vVec = CetvelMathService._tempVVec;
          if (Math.abs(axis.x) < 0.9) {
            uVec.set(1, 0, 0);
            uVec.crossVectors(axis, uVec).normalize();
          } else {
            uVec.set(0, 1, 0);
            uVec.crossVectors(axis, uVec).normalize();
          }
          vVec.crossVectors(axis, uVec).normalize();

          // ADIM 4: Centroid ve 2D projeksiyon - 🚀 ZERO-ALLOCATION
          const centroid = CetvelMathService._tempCentroid.set(0, 0, 0);
          for (let i = 0; i < vertexCount; i++) {
            const i3 = i * 3;
            centroid.x += posArray[i3];
            centroid.y += posArray[i3 + 1];
            centroid.z += posArray[i3 + 2];
          }
          centroid.divideScalar(vertexCount);

          const projected2D: { x: number; y: number }[] = [];
          const rel = CetvelMathService._tempRel;
          for (let i = 0; i < vertexCount; i++) {
            const i3 = i * 3;
            rel.set(posArray[i3] - centroid.x, posArray[i3 + 1] - centroid.y, posArray[i3 + 2] - centroid.z);
            const axisProj = rel.dot(axis);
            rel.x -= axis.x * axisProj;
            rel.y -= axis.y * axisProj;
            rel.z -= axis.z * axisProj;
            projected2D.push({ x: rel.dot(uVec), y: rel.dot(vVec) });
          }

          // ADIM 5: 2D Circle Fitting (Kasa metodu)
          let sumX = 0, sumY = 0, sumX2 = 0, sumY2 = 0, sumXY = 0;
          let sumX3 = 0, sumY3 = 0, sumX2Y = 0, sumXY2 = 0;

          projected2D.forEach(p => {
            sumX += p.x; sumY += p.y;
            sumX2 += p.x * p.x; sumY2 += p.y * p.y;
            sumXY += p.x * p.y;
            sumX3 += p.x ** 3; sumY3 += p.y ** 3;
            sumX2Y += p.x * p.x * p.y; sumXY2 += p.x * p.y * p.y;
          });

          const N = projected2D.length;
          const A = N * sumX2 - sumX * sumX;
          const B = N * sumXY - sumX * sumY;
          const C = N * sumY2 - sumY * sumY;
          const D = 0.5 * (N * sumX3 + N * sumXY2 - sumX * sumX2 - sumX * sumY2);
          const E = 0.5 * (N * sumX2Y + N * sumY3 - sumY * sumX2 - sumY * sumY2);

          const denom = A * C - B * B;
          if (Math.abs(denom) < 1e-10) {
            // Fallback: ağırlık merkezi
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          let cx2d = (D * C - B * E) / denom;
          let cy2d = (A * E - B * D) / denom;

          // Geometrik refinement (Gauss-Newton iterasyonu)
          // 🚀 ULTRA PERF: Iteration count (10 → 6) + early convergence check
          const maxGNIterations = 6; // 10'dan 6'ya düşürdük (yeterince doğru)
          const gnConvergenceThreshold = 1e-8;

          for (let iter = 0; iter < maxGNIterations; iter++) {
            let rSum = 0;
            // 🚀 PERF: forEach → for loop (küçük optimizasyon ama her miktar sayar)
            for (let i = 0; i < projected2D.length; i++) {
              const p = projected2D[i];
              rSum += Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
            }
            const rMean = rSum / N;

            let gradX = 0, gradY = 0;
            for (let i = 0; i < projected2D.length; i++) {
              const p = projected2D[i];
              const dx = p.x - cx2d;
              const dy = p.y - cy2d;
              const ri = Math.sqrt(dx * dx + dy * dy);
              if (ri > 0.001) {
                const err = ri - rMean;
                gradX += err * (-dx / ri);
                gradY += err * (-dy / ri);
              }
            }

            const step = 0.5 / N;
            const newCx = cx2d - step * gradX;
            const newCy = cy2d - step * gradY;

            // 🚀 EARLY EXIT: Converged check
            const centerShift = Math.abs(newCx - cx2d) + Math.abs(newCy - cy2d);
            cx2d = newCx;
            cy2d = newCy;

            if (centerShift < gnConvergenceThreshold) {
              break; // Converged!
            }
          }

          // Final yarıçap: Median kullan
          const allRadii: number[] = [];
          projected2D.forEach(p => {
            const r = Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
            allRadii.push(r);
          });
          allRadii.sort((a, b) => a - b);
          const medianIndex = Math.floor(allRadii.length / 2);
          const gercekYaricap = allRadii[medianIndex];

          // ADIM 6: 3D merkez hesapla - 🚀 ALLOCATION GEREKLI (return value)
          // NOT: Bu fonksiyondan döndürülen değer olduğu için yeni allocation gerekli
          const gercekMerkez = new THREE.Vector3()
            .copy(centroid)
            .addScaledVector(uVec, cx2d)
            .addScaledVector(vVec, cy2d);

          // Geçerlilik kontrolü
          if (isNaN(gercekYaricap) || gercekYaricap < 0.1 || gercekYaricap > 100000) {
            const merkez = this.geomService.agirlikMerkezi(yuzeyGeom);
            return { merkez, yaricap: yuzeyTip.icerik.avgRadius, tip: tipi };
          }

          // 🚀 CACHE STORE: Sonucu cache'e kaydet (bir daha hesaplamaya gerek kalmaz!)
          const cacheEntry: CachedGeometryAnalysis = {
            surfaceType: tipi as any,
            accuracy: 95, // High accuracy (2D circle fitting)
            avgRadius: gercekYaricap,
            minRadius: gercekYaricap * 0.95, // Approximate
            maxRadius: gercekYaricap * 1.05, // Approximate
            circleCenter: new FikoVektor3D(gercekMerkez.x, gercekMerkez.y, gercekMerkez.z),
            circleRMS: 0, // Not calculated here
            axis: new FikoVektor3D(axis.x, axis.y, axis.z),
            vertexCount: vertexCount,
            hashedBounds: '', // Will be generated by cache
            timestamp: Date.now()
          };
          this.geometryCache.set(yuzeyGeom, cacheEntry);

          return {
            merkez: gercekMerkez,
            yaricap: gercekYaricap,
            tip: tipi
          };
        }
      }
    } catch (e) {
      // Hata durumunda sessizce null döndür
    }
    return null;
  }

  /**
   * 🔴 BUGFIX: mm'den seçilen birime dönüştür
   */
  private birimDonustur(deger: number, kaynakBirim: string, hedefBirim: string, birimFaktore: { [key: string]: number }): number {
    if (kaynakBirim === hedefBirim) {
      return deger;
    }
    // mm'yi hedef birime çevir
    const faktor = birimFaktore[hedefBirim] || 1;
    return deger * faktor;
  }

  /**
   * 🚀 Cetvel ölçüm hesaplaması - WASM hızlandırmalı
   * Tüm kombinasyonlar için merkezi hesaplama
   */
  async hesapla(
    // Seçim verileri
    secilen1: any,
    secilen2: any,
    secilen1PointIndex: number | null,
    secilen2PointIndex: number | null,
    secilen1Geom: THREE.BufferGeometry | null,
    secilen2Geom: THREE.BufferGeometry | null,
    sonuc1Tipi: string,
    sonuc2Tipi: string,
    secim1Turu: string,
    secim2Turu: string,
    fikoSecim1: any,
    fikoSecim2: any,
    anaGosterim: any,
    tolerans: number,
    olcumGecmisi: any[],
    // Callback fonksiyonları
    olcumGecmisineEkleCallback: (aciklama: string, olcu: number, birim: string, tip: string) => void,
    gosterimKayniyaEkleCallback: (nokta1: THREE.Vector3, nokta2: THREE.Vector3, label: string, isArc?: boolean, aci?: number, merkezNoktasi?: THREE.Vector3) => void,
    detectChangesCallback: () => void,
    // 🔴 BUGFIX: Basamak sayıları
    mesafeBas: number = 2,
    aciBasamak: number = 1,
    // 🔴 BUGFIX: Birim ve dönüştürme
    secilenBirim: string = 'mm',
    birimFaktore: { [key: string]: number } = { 'mm': 1, 'cm': 0.1, 'inch': 0.03937, 'm': 0.001 },
    // 🎯 ÖLÇÜM MODU: Radiüslü seçimler için enKisaDuz/enKisa/merkez/enUzak/enUzakDuz mesafe toggle
    secim1OlcumModu: 'enKisa' | 'enKisaDuz' | 'merkez' | 'enUzak' | 'enUzakDuz' = 'enKisaDuz',
    secim2OlcumModu: 'enKisa' | 'enKisaDuz' | 'merkez' | 'enUzak' | 'enUzakDuz' = 'enKisaDuz',
    secim1MerkezBilgisi: { merkez: THREE.Vector3, yaricap: number, tip?: string } | null = null,
    secim2MerkezBilgisi: { merkez: THREE.Vector3, yaricap: number, tip?: string } | null = null
  ): Promise<any> {

    if (!secilen1 || !secilen2) {
      detectChangesCallback();
      return null;
    }

    let sonuc: any = null;

    // 🎯 ÖLÇÜM MODU: Merkez modu aktifse, seçimi "Nokta" gibi davran
    // Merkez noktasını kullanarak hesaplama yap
    let efektifTip1 = sonuc1Tipi;
    let efektifTip2 = sonuc2Tipi;
    let merkezNokta1: THREE.Vector3 | null = null;
    let merkezNokta2: THREE.Vector3 | null = null;

    // 🎯 MERKEZ MODU: Sadece merkez modunda nokta gibi davran
    if (secim1OlcumModu === 'merkez' && secim1MerkezBilgisi?.merkez) {
      efektifTip1 = "Nokta";
      merkezNokta1 = secim1MerkezBilgisi.merkez.clone();
    }
    if (secim2OlcumModu === 'merkez' && secim2MerkezBilgisi?.merkez) {
      efektifTip2 = "Nokta";
      merkezNokta2 = secim2MerkezBilgisi.merkez.clone();
    }

    /** NOT: Nokta - Nokta Mesafesi */
    if (efektifTip1 == "Nokta" && efektifTip2 == "Nokta") {

      let nokta1: THREE.Vector3 = new THREE.Vector3();
      let nokta2: THREE.Vector3 = new THREE.Vector3();

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta1) {
        nokta1 = merkezNokta1.clone();
      }
      // Nokta 1'i al - Index'i highlight'tan al, koordinatı orijinal geometry'den
      else if (secilen1.object?.type === "Points" || secilen1.altObje?.type === "Points") {
        if (secilen1PointIndex !== null) {
          const f = (secilen1.altObje || secilen1.object) as THREE.Points;
          const positions = f.geometry.attributes["position"];

          // Saklanmış index'den orijinal geometry'nin vertex'ini al
          nokta1 = new THREE.Vector3(
            positions.getX(secilen1PointIndex),
            positions.getY(secilen1PointIndex),
            positions.getZ(secilen1PointIndex)
          );

          // Orijinal Points object'inin STABLE transform'unu uygula (sahne eklendikten sonra değişmiyor)
          const matrix = (f as any).matrixWorld || (f.parent as any)?.matrixWorld;
          if (matrix) {
            nokta1.applyMatrix4(matrix);
          }
        } else {
          nokta1 = secilen1.point?.clone() || new THREE.Vector3();
        }
      } else {
        nokta1 = secilen1.point?.clone() || new THREE.Vector3();
      }

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta2) {
        nokta2 = merkezNokta2.clone();
      }
      // Nokta 2'yi al - Index'i highlight'tan al, koordinatı orijinal geometry'den
      else if (secilen2.object?.type === "Points" || secilen2.altObje?.type === "Points") {
        if (secilen2PointIndex !== null) {
          const f = (secilen2.altObje || secilen2.object) as THREE.Points;
          const positions = f.geometry.attributes["position"];

          // Saklanmış index'den orijinal geometry'nin vertex'ini al
          nokta2 = new THREE.Vector3(
            positions.getX(secilen2PointIndex),
            positions.getY(secilen2PointIndex),
            positions.getZ(secilen2PointIndex)
          );

          // Orijinal Points object'inin STABLE transform'unu uygula (sahne eklendikten sonra değişmiyor)
          const matrix = (f as any).matrixWorld || (f.parent as any)?.matrixWorld;
          if (matrix) {
            nokta2.applyMatrix4(matrix);
          }
        } else {
          nokta2 = secilen2.point?.clone() || new THREE.Vector3();
        }
      } else {
        nokta2 = secilen2.point?.clone() || new THREE.Vector3();
      }

      const mesafe = nokta1.distanceTo(nokta2);

      // Geçmişe ekle
      olcumGecmisineEkleCallback("Uzaklık", mesafe, "mm", "Nokta-Nokta");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Uzaklık");
      const toleransKontrolSonucu = this.geomService.toleransKontrol(mesafe, tolerans);

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Uzaklık",
          olcu: mesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: mesafe - tolerans,
          toleransMax: mesafe + tolerans
        }
      };

      // Arrow line çiz
      const mesafedonusmus = this.birimDonustur(mesafe, 'mm', secilenBirim, birimFaktore);
      gosterimKayniyaEkleCallback(nokta1, nokta2, mesafedonusmus.toFixed(mesafeBas) + ' ' + secilenBirim);

      detectChangesCallback();
    }

    /** NOT: Nokta - Çizgi Mesafesi */
    if ((efektifTip1 == "Nokta" && efektifTip2 == "Çizgi")) {

      // Yedeklenen çizgi geometrisini kullan
      let cizgiGeometri = secilen2Geom;

      if (!cizgiGeometri) {
        return null;
      }


      let nokta: THREE.Vector3 = new THREE.Vector3();

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta1) {
        nokta = merkezNokta1.clone();
      } else if (secim1Turu != "Nokta Koy" && (secilen1.object?.type === "Points" || secilen1.altObje?.type === "Points")) {
        const f = (secilen1.altObje || secilen1.object) as THREE.Points;
        // Saklanmış index'i kullan (closestPointIndex)
        const pointIndex = secilen1PointIndex !== null ? secilen1PointIndex : secilen1.index;
        nokta = new FikoVektor3D(
          f.geometry.attributes["position"].getX(pointIndex),
          f.geometry.attributes["position"].getY(pointIndex),
          f.geometry.attributes["position"].getZ(pointIndex)
        );
        if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4((f.parent as any).matrixWorld);
        }
      } else if (secilen1.object?.type === "Points" || secilen1.altObje?.type === "Points") {
        // Kesişim noktasına en yakın geometri noktasını bul
        const f = (secilen1.altObje || secilen1.object) as THREE.Points;
        const intersectionPoint = secilen1.point?.clone() || new THREE.Vector3();
        // Kesişim noktasını local koordinatlara çevir
        const invMatrix = new THREE.Matrix4().copy((f as any).matrixWorld || (f.parent as any)?.matrixWorld || new THREE.Matrix4()).invert();
        intersectionPoint.applyMatrix4(invMatrix);

        const positions = f.geometry.attributes["position"];
        let minDist = Infinity;
        let closestIndex = 0;

        for (let i = 0; i < positions.count; i++) {
          const x = positions.getX(i);
          const y = positions.getY(i);
          const z = positions.getZ(i);
          const dist = intersectionPoint.distanceTo(new THREE.Vector3(x, y, z));
          if (dist < minDist) {
            minDist = dist;
            closestIndex = i;
          }
        }

        nokta = new FikoVektor3D(
          positions.getX(closestIndex),
          positions.getY(closestIndex),
          positions.getZ(closestIndex)
        );
        if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4(f.parent.matrixWorld);
        } else if ((f as any).matrixWorld) {
          nokta.applyMatrix4((f as any).matrixWorld);
        }
      } else {
        nokta = secilen1.point?.clone() || new THREE.Vector3();
      }

      const mesafe = this.geomService.alCizgiNoktaMesafe(cizgiGeometri, nokta);

      // Geçmişe ekle
      olcumGecmisineEkleCallback("Uzaklık", mesafe, "mm", "Nokta-Çizgi");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Uzaklık");

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Uzaklık",
          olcu: mesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: mesafe - tolerans,
          toleransMax: mesafe + tolerans
        }
      };

      // Arrow line çiz - nokta ile çizgi üzerindeki en yakın nokta arasında (DİK ÖLÇÜM)
      if (cizgiGeometri) {
        const projeNokta = this.geomService.alCizgiProjeNokta(cizgiGeometri, nokta);
        if (projeNokta && projeNokta.nokta) {
          const mesafedonusmus = this.birimDonustur(mesafe, 'mm', secilenBirim, birimFaktore);
          gosterimKayniyaEkleCallback(nokta, projeNokta.nokta, mesafedonusmus.toFixed(mesafeBas) + ' ' + secilenBirim);
        }
      }

      detectChangesCallback();
    }

    /** NOT: Çizgi - Nokta Mesafesi */
    if ((efektifTip1 == "Çizgi" && efektifTip2 == "Nokta")) {

      // Yedeklenen çizgi geometrisini kullan
      let cizgiGeometri = secilen1Geom;

      if (!cizgiGeometri) {
        return null;
      }


      let nokta: THREE.Vector3 = new THREE.Vector3();

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta2) {
        nokta = merkezNokta2.clone();
      } else if (secim2Turu != "Nokta Koy" && (secilen2.object?.type === "Points" || secilen2.altObje?.type === "Points")) {
        const f = (secilen2.altObje || secilen2.object) as THREE.Points;
        const pointIndex = secilen2PointIndex !== null ? secilen2PointIndex : secilen2.index;
        nokta = new FikoVektor3D(
          f.geometry.attributes["position"].getX(pointIndex),
          f.geometry.attributes["position"].getY(pointIndex),
          f.geometry.attributes["position"].getZ(pointIndex)
        );
        if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4((f.parent as any).matrixWorld);
        }
      } else if (secilen2.object?.type === "Points" || secilen2.altObje?.type === "Points") {
        // Kesişim noktasına en yakın geometri noktasını bul
        const f = (secilen2.altObje || secilen2.object) as THREE.Points;
        const intersectionPoint = secilen2.point?.clone() || new THREE.Vector3();
        // Kesişim noktasını local koordinatlara çevir
        const invMatrix = new THREE.Matrix4().copy((f as any).matrixWorld || (f.parent as any)?.matrixWorld || new THREE.Matrix4()).invert();
        intersectionPoint.applyMatrix4(invMatrix);

        const positions = f.geometry.attributes["position"];
        let minDist = Infinity;
        let closestIndex = 0;

        for (let i = 0; i < positions.count; i++) {
          const x = positions.getX(i);
          const y = positions.getY(i);
          const z = positions.getZ(i);
          const dist = intersectionPoint.distanceTo(new THREE.Vector3(x, y, z));
          if (dist < minDist) {
            minDist = dist;
            closestIndex = i;
          }
        }

        nokta = new FikoVektor3D(
          positions.getX(closestIndex),
          positions.getY(closestIndex),
          positions.getZ(closestIndex)
        );
        if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4(f.parent.matrixWorld);
        } else if ((f as any).matrixWorld) {
          nokta.applyMatrix4((f as any).matrixWorld);
        }
      } else {
        nokta = secilen2.point?.clone() || new THREE.Vector3();
      }

      const mesafe = this.geomService.alCizgiNoktaMesafe(cizgiGeometri, nokta);

      // Geçmişe ekle
      olcumGecmisineEkleCallback("Uzaklık", mesafe, "mm", "Çizgi-Nokta");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Uzaklık");

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Uzaklık",
          olcu: mesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: mesafe - tolerans,
          toleransMax: mesafe + tolerans
        }
      };

      // Arrow line çiz - çizgi üzerindeki nokta ile seçilen nokta arasında (DİK ÖLÇÜM)
      if (cizgiGeometri) {
        const projeNokta = this.geomService.alCizgiProjeNokta(cizgiGeometri, nokta);
        if (projeNokta && projeNokta.nokta) {
          const mesafedonusmus = this.birimDonustur(mesafe, 'mm', secilenBirim, birimFaktore);
          gosterimKayniyaEkleCallback(projeNokta.nokta, nokta, mesafedonusmus.toFixed(mesafeBas) + ' ' + secilenBirim);
        }
      }

      detectChangesCallback();
    }

    /** NOT: Yüzey - Yüzey Mesafesi (Açılı ise açı, değilse mesafe) */
    if (efektifTip1 == "Yüzey" && efektifTip2 == "Yüzey") {

      // Yedeklenen geometrileri kullan
      let yuzey1Geom = secilen1Geom;
      let yuzey2Geom = secilen2Geom;


      // Eğer geometri yedeklenmemişse fallback (bu nadiren olacak, yedekleme sırasında transform zaten uygulanmış)
      if (!yuzey1Geom || !yuzey2Geom) {
        let yuzey1: THREE.Mesh = (secilen1?.altObje || secilen1?.object) as THREE.Mesh;
        let yuzey2: THREE.Mesh = (secilen2?.altObje || secilen2?.object) as THREE.Mesh;

        if (!yuzey1Geom) {
          yuzey1Geom = yuzey1.geometry?.clone();
          if (!yuzey1Geom) {
            yuzey1Geom = fikoSecim1.sonSecilenHighlightGeometriYuzey?.clone();
          }
          if (!yuzey1Geom && secilen1?.normal) {
            const normal1 = (secilen1.normal as THREE.Vector3).clone().normalize();
            const point1 = secilen1.point as THREE.Vector3;
            yuzey1Geom = this.geomService.normalDanPlaneGeometriOlustur(normal1, point1, 1000);
          }
          // Transform uygula sadece direct geometry alındıysa
          if (yuzey1Geom && yuzey1?.parent && (yuzey1.parent as any).matrixWorld) {
            yuzey1Geom.applyMatrix4((yuzey1.parent as any).matrixWorld);
          }
        }

        if (!yuzey2Geom) {
          yuzey2Geom = yuzey2.geometry?.clone();
          if (!yuzey2Geom) {
            yuzey2Geom = fikoSecim2.sonSecilenHighlightGeometriYuzey?.clone();
          }
          if (!yuzey2Geom && secilen2?.normal) {
            const normal2 = (secilen2.normal as THREE.Vector3).clone().normalize();
            const point2 = secilen2.point as THREE.Vector3;
            yuzey2Geom = this.geomService.normalDanPlaneGeometriOlustur(normal2, point2, 1000);
          }
          // Transform uygula sadece direct geometry alındıysa
          if (yuzey2Geom && yuzey2?.parent && (yuzey2.parent as any).matrixWorld) {
            yuzey2Geom.applyMatrix4((yuzey2.parent as any).matrixWorld);
          }
        }
      }


      if (!yuzey1Geom || !yuzey2Geom) {
        return null;
      }

      // Clone etmek istemiyoruz çünkü zaten yedeklenmiş
      const geom1 = yuzey1Geom;
      const geom2 = yuzey2Geom;


      // Normal vektörlerini al
      const duzlem1 = this.geomService.yuzeyDuzlemAl(geom1);
      const duzlem2 = this.geomService.yuzeyDuzlemAl(geom2);


      if (!duzlem1 || !duzlem2 || !duzlem1.normal || !duzlem2.normal) {
        return null;
      }

      // Normal vektörler arası açıyı hesapla
      const normal1 = (duzlem1.normal as THREE.Vector3).clone().normalize();
      const normal2 = (duzlem2.normal as THREE.Vector3).clone().normalize();


      // Dot product ile açı hesapla
      const dotProduct = Math.abs(normal1.dot(normal2));
      const radiyan = Math.acos(Math.min(1, dotProduct)); // Clamp to avoid NaN
      const derece = (radiyan * 180) / Math.PI;


      // Ağırlık merkezleri arası vektörü al
      const merkez1 = this.geomService.agirlikMerkezi(geom1);
      const merkez2 = this.geomService.agirlikMerkezi(geom2);
      const merkezVektoru = merkez2.clone().sub(merkez1);


      // Paralel ise (açı ~ 0 derece), iki plane arasındaki mesafeyi hesapla
      // Eğer açılı ise, kesişim çizgisine dik en kısa mesafe
      let mesafe: number;

      if (derece < 0.001) { // Neredeyse paralel (5 dereceden küçük açı)
        // Paralel durumda: iki düzlem arasındaki mesafeyi hesapla
        // Düzlem 1: normal · point = constant1
        // Düzlem 2: normal · point = constant2
        // İki paralel düzlem arasındaki mesafe (normal normalized) = |normal · (merkez2 - merkez1)|
        const vektorArası = merkez2.clone().sub(merkez1);
        mesafe = Math.abs(normal1.dot(vektorArası));
      } else {
        // Açılı durumda: iki düzlem arasındaki en kısa mesafe
        // Tüm geometri yüzeylerini kontrol et (sadece vertices değil)
        // 🚀 GPU-accelerated hesaplama
        mesafe = await this.geomService.enKisaMesafeHesapla(geom1, geom2);
      }

      // Tolerans: mesafe 0.0001 mm'den büyükse farklı yüzeyler, yoksa aynı yüzey
      const toleransMesafe = 0.0001; // 0.0001 mm tolerans

      // Tıklanan noktaları dışarıda tanımla
      const cizgiNokta1 = secilen1.point.clone();
      const cizgiNokta2 = secilen2.point.clone();

      // 🔵 Yüzey tiplerini tespit et (düzlem mi, eğri mi?)
      const yuzey1Tip = yuzeyTipiTespit(geom1);
      const yuzey2Tip = yuzeyTipiTespit(geom2);
      const yuzey1Duzlem = yuzey1Tip?.tip === 'duzlem';
      const yuzey2Duzlem = yuzey2Tip?.tip === 'duzlem';

      let enKisaMesafe = Infinity;
      let enYakinNokta1 = new THREE.Vector3();
      let enYakinNokta2 = new THREE.Vector3();
      // 🎯 EN UZAK MESAFE: Geometriden gerçek en uzak noktaları bul (CATIA style)
      let enUzakMesafe = 0;
      let enUzakNokta1 = new THREE.Vector3();
      let enUzakNokta2 = new THREE.Vector3();

      const yuzey1Positions = geom1.attributes.position.array as Float32Array;
      const yuzey2Positions = geom2.attributes.position.array as Float32Array;

      // 🎯 DÜZLEM-DÜZLEM: Dik mesafe hesapla
      if (yuzey1Duzlem && yuzey2Duzlem) {
        // Her iki yüzey de düzlem - dik projeksiyon yap
        for (let i = 0; i < yuzey1Positions.length; i += 3) {
          const vertex1 = new THREE.Vector3(yuzey1Positions[i], yuzey1Positions[i + 1], yuzey1Positions[i + 2]);
          const dikMesafeTemp = Math.abs(duzlem2.distanceToPoint(vertex1));

          // En kısa
          if (dikMesafeTemp < enKisaMesafe) {
            enKisaMesafe = dikMesafeTemp;
            enYakinNokta1 = vertex1.clone();
            const projNokta = new THREE.Vector3();
            duzlem2.projectPoint(vertex1, projNokta);
            enYakinNokta2 = projNokta;
          }
          // En uzak
          if (dikMesafeTemp > enUzakMesafe) {
            enUzakMesafe = dikMesafeTemp;
            enUzakNokta1 = vertex1.clone();
            const projNokta = new THREE.Vector3();
            duzlem2.projectPoint(vertex1, projNokta);
            enUzakNokta2 = projNokta;
          }
        }

        for (let i = 0; i < yuzey2Positions.length; i += 3) {
          const vertex2 = new THREE.Vector3(yuzey2Positions[i], yuzey2Positions[i + 1], yuzey2Positions[i + 2]);
          const dikMesafeTemp = Math.abs(duzlem1.distanceToPoint(vertex2));

          // En kısa
          if (dikMesafeTemp < enKisaMesafe) {
            enKisaMesafe = dikMesafeTemp;
            enYakinNokta2 = vertex2.clone();
            const projNokta = new THREE.Vector3();
            duzlem1.projectPoint(vertex2, projNokta);
            enYakinNokta1 = projNokta;
          }
          // En uzak
          if (dikMesafeTemp > enUzakMesafe) {
            enUzakMesafe = dikMesafeTemp;
            enUzakNokta2 = vertex2.clone();
            const projNokta = new THREE.Vector3();
            duzlem1.projectPoint(vertex2, projNokta);
            enUzakNokta1 = projNokta;
          }
        }
      }
      // 🎯 DÜZLEM-EĞRİ: Eğri yüzeyden düzleme dik mesafe
      else if (yuzey1Duzlem && !yuzey2Duzlem) {
        // Yüzey 1 düzlem, Yüzey 2 eğri - Yüzey 2'nin noktalarından Yüzey 1'e dik mesafe
        for (let i = 0; i < yuzey2Positions.length; i += 3) {
          const vertex2 = new THREE.Vector3(yuzey2Positions[i], yuzey2Positions[i + 1], yuzey2Positions[i + 2]);
          const dikMesafeTemp = Math.abs(duzlem1.distanceToPoint(vertex2));

          // En kısa
          if (dikMesafeTemp < enKisaMesafe) {
            enKisaMesafe = dikMesafeTemp;
            enYakinNokta2 = vertex2.clone();
            const projNokta = new THREE.Vector3();
            duzlem1.projectPoint(vertex2, projNokta);
            enYakinNokta1 = projNokta;
          }
          // En uzak
          if (dikMesafeTemp > enUzakMesafe) {
            enUzakMesafe = dikMesafeTemp;
            enUzakNokta2 = vertex2.clone();
            const projNokta = new THREE.Vector3();
            duzlem1.projectPoint(vertex2, projNokta);
            enUzakNokta1 = projNokta;
          }
        }
      }
      // 🎯 EĞRİ-DÜZLEM: Eğri yüzeyden düzleme dik mesafe
      else if (!yuzey1Duzlem && yuzey2Duzlem) {
        // Yüzey 1 eğri, Yüzey 2 düzlem - Yüzey 1'in noktalarından Yüzey 2'ye dik mesafe
        for (let i = 0; i < yuzey1Positions.length; i += 3) {
          const vertex1 = new THREE.Vector3(yuzey1Positions[i], yuzey1Positions[i + 1], yuzey1Positions[i + 2]);
          const dikMesafeTemp = Math.abs(duzlem2.distanceToPoint(vertex1));

          // En kısa
          if (dikMesafeTemp < enKisaMesafe) {
            enKisaMesafe = dikMesafeTemp;
            enYakinNokta1 = vertex1.clone();
            const projNokta = new THREE.Vector3();
            duzlem2.projectPoint(vertex1, projNokta);
            enYakinNokta2 = projNokta;
          }
          // En uzak
          if (dikMesafeTemp > enUzakMesafe) {
            enUzakMesafe = dikMesafeTemp;
            enUzakNokta1 = vertex1.clone();
            const projNokta = new THREE.Vector3();
            duzlem2.projectPoint(vertex1, projNokta);
            enUzakNokta2 = projNokta;
          }
        }
      }
      // 🎯 EĞRİ-EĞRİ: En kısa/uzak mesafe (noktalar arası) - 🚀 GPU ACCELERATED
      else {
        // Her iki yüzey de eğri - GPU ile en yakın noktalar arası mesafe
        try {
          const gpuResult = await this.geomService.enYakinNoktaCiftiHesapla(geom1, geom2);
          enKisaMesafe = gpuResult.mesafe;
          enYakinNokta1 = gpuResult.nokta1;
          enYakinNokta2 = gpuResult.nokta2;
        } catch (e) {
          // CPU Fallback - ignore
        }
        // En uzak mesafe için CPU ile hesapla (eğri-eğri durumu)
        for (let i = 0; i < yuzey1Positions.length; i += 3) {
          const vertex1 = new THREE.Vector3(yuzey1Positions[i], yuzey1Positions[i + 1], yuzey1Positions[i + 2]);

          for (let j = 0; j < yuzey2Positions.length; j += 3) {
            const vertex2 = new THREE.Vector3(yuzey2Positions[j], yuzey2Positions[j + 1], yuzey2Positions[j + 2]);
            const mesafeTemp = vertex1.distanceTo(vertex2);

            // En kısa (GPU fallback)
            if (enKisaMesafe === Infinity && mesafeTemp < enKisaMesafe) {
              enKisaMesafe = mesafeTemp;
              enYakinNokta1 = vertex1.clone();
              enYakinNokta2 = vertex2.clone();
            }
            // En uzak
            if (mesafeTemp > enUzakMesafe) {
              enUzakMesafe = mesafeTemp;
              enUzakNokta1 = vertex1.clone();
              enUzakNokta2 = vertex2.clone();
            }
          }
        }
      }

      // Eski değişken adını koru (uyumluluk için)
      const enKisaDikMesafe = enKisaMesafe;

      // Açı bilgisi
      const aci = Math.min(derece, 180 - derece);

      // 🔵 SİLİNDİR/KÜRE TESPİTİ - Merkez bilgisi
      const yuzey1Silindir = this.yuzeySilindirTespit(geom1);
      const yuzey2Silindir = this.yuzeySilindirTespit(geom2);

      // 🎯 PARALEL DEĞİL TESPİTİ: 0.001 dereceden büyük açı = paralel değil
      const yuzeylerParalelDegil = derece > 0.001;

      if (derece > 0.001) {
        // AÇILI YÜZEYLER - AÇI VE EN KISA DİK MESAFE GÖSTER

        // Geçmişe ekle
        olcumGecmisineEkleCallback("Yüzeyler Arası Mesafe", enKisaDikMesafe, "mm", "Yüzey-Yüzey (En Kısa Dik)");

        // Min/Max/Ortalama hesapla
        const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Yüzeyler Arası Mesafe");

        sonuc = {
          turu: 'Mesafe',
          mesafe: {
            aciklama: `Yüzeyler Arası Mesafe (En Kısa Dik)`,
            olcu: enKisaDikMesafe,
            birim: "mm",
            min: minMaxOrta?.min,
            max: minMaxOrta?.max,
            ortalama: minMaxOrta?.ortalama,
            toleransMin: enKisaDikMesafe - tolerans,
            toleransMax: enKisaDikMesafe + tolerans,
            aci: aci,
            // 🎯 PARALEL DEĞİL: Yüzeyler paralel değilse uyarı flag'i
            paralelDegil: yuzeylerParalelDegil,
            // 🔵 Merkez bilgileri
            yuzey1Merkez: yuzey1Silindir?.merkez,
            yuzey1Yaricap: yuzey1Silindir?.yaricap,
            yuzey1Tip: yuzey1Silindir?.tip,
            yuzey2Merkez: yuzey2Silindir?.merkez,
            yuzey2Yaricap: yuzey2Silindir?.yaricap,
            yuzey2Tip: yuzey2Silindir?.tip
          }
        };
      } else {
        // PARALEL YÜZEYLER - MESAFE GÖSTER

        // Geçmişe ekle
        olcumGecmisineEkleCallback("Paralel Yüzeyler Arası Mesafe", enKisaDikMesafe, "mm", "Yüzey-Yüzey (Dik Mesafe)");

        // Min/Max/Ortalama hesapla
        const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Paralel Yüzeyler Arası Mesafe");

        sonuc = {
          turu: 'Mesafe',
          mesafe: {
            aciklama: "Paralel Yüzeyler Arası Mesafe",
            olcu: enKisaDikMesafe,
            birim: "mm",
            min: minMaxOrta?.min,
            max: minMaxOrta?.max,
            ortalama: minMaxOrta?.ortalama,
            toleransMin: enKisaDikMesafe - tolerans,
            toleransMax: enKisaDikMesafe + tolerans,
            // 🎯 PARALEL DEĞİL: Paralel yüzeylerde false
            paralelDegil: false,
            // 🔵 Merkez bilgileri
            yuzey1Merkez: yuzey1Silindir?.merkez,
            yuzey1Yaricap: yuzey1Silindir?.yaricap,
            yuzey1Tip: yuzey1Silindir?.tip,
            yuzey2Merkez: yuzey2Silindir?.merkez,
            yuzey2Yaricap: yuzey2Silindir?.yaricap,
            yuzey2Tip: yuzey2Silindir?.tip
          }
        };
      }

      // 🎯 ÖLÇÜM MODU: Ölçüm noktalarını belirle
      // enKisa: en yakın nokta, merkez: merkez noktası, enUzak: en uzak nokta (düzleme dik)
      let olcumNokta1 = enYakinNokta1.clone();
      let olcumNokta2 = enYakinNokta2.clone();

      // Seçim 1 için ölçüm modu kontrolü
      if (secim1OlcumModu === 'merkez' && secim1MerkezBilgisi?.merkez) {
        olcumNokta1 = secim1MerkezBilgisi.merkez.clone();
      } else if (secim1OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta1 = enUzakNokta1.clone();
      } else if (secim1OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim2MerkezBilgisi.merkez, secim1MerkezBilgisi.merkez).normalize();
          let enUzakProj = -Infinity;
          for (let i = 0; i < yuzey1Positions.length; i += 3) {
            const vertex = new THREE.Vector3(yuzey1Positions[i], yuzey1Positions[i + 1], yuzey1Positions[i + 2]);
            const proj = vertex.clone().sub(secim1MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta1 = vertex.clone();
            }
          }
        } else {
          olcumNokta1 = enUzakNokta1.clone();
        }
      }

      // Seçim 2 için ölçüm modu kontrolü
      if (secim2OlcumModu === 'merkez' && secim2MerkezBilgisi?.merkez) {
        olcumNokta2 = secim2MerkezBilgisi.merkez.clone();
      } else if (secim2OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta2 = enUzakNokta2.clone();
      } else if (secim2OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim1MerkezBilgisi.merkez, secim2MerkezBilgisi.merkez).normalize();
          let enUzakProj = -Infinity;
          for (let i = 0; i < yuzey2Positions.length; i += 3) {
            const vertex = new THREE.Vector3(yuzey2Positions[i], yuzey2Positions[i + 1], yuzey2Positions[i + 2]);
            const proj = vertex.clone().sub(secim2MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta2 = vertex.clone();
            }
          }
        } else {
          olcumNokta2 = enUzakNokta2.clone();
        }
      }

      // 🎯 EN UZAK + DÜZLEM: Düzlem tarafında dik projeksiyon yap (CATIA style)
      if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak' || secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
        // Yüzey 1 düzlem ise, olcumNokta2'yi düzleme dik projekte et
        if (yuzey1Duzlem && duzlem1 && !secim1MerkezBilgisi) {
          const projNokta = new THREE.Vector3();
          duzlem1.projectPoint(olcumNokta2, projNokta);
          olcumNokta1 = projNokta;
        }
        // Yüzey 2 düzlem ise, olcumNokta1'i düzleme dik projekte et
        if (yuzey2Duzlem && duzlem2 && !secim2MerkezBilgisi) {
          const projNokta = new THREE.Vector3();
          duzlem2.projectPoint(olcumNokta1, projNokta);
          olcumNokta2 = projNokta;
        }
      }

      // 🎯 MANTIKLI UÇ NOKTA (paralel düzlemler): Eski kod ölçü çizgisini yüzeyin rastgele bir
      // KÖŞE vertex'inden başlatıyordu → çizgi alakasız yerden çıkıyordu. İki yüzey de düzlem ve
      // ~paralel ise, çizgiyi KULLANICININ TIKLADIĞI noktadan başlat ve karşı düzleme DİK ayağıyla
      // bağla → temsili/okunaklı yerden çıkar. Paralel düzlemde dik mesafe her noktadan AYNI olduğu
      // için gösterilen değer DEĞİŞMEZ (güvenli). Yalnız varsayılan en-kısa modlarında uygulanır.
      const _vrsylModlar = (secim1OlcumModu === 'enKisa' || secim1OlcumModu === 'enKisaDuz')
                        && (secim2OlcumModu === 'enKisa' || secim2OlcumModu === 'enKisaDuz');
      // AÇI EŞİĞİ: ~2°'ye kadar az-açılı yüzeyleri de kapsa (kullanıcının 1.2° durumu dahil).
      // Bu aralıkta dik mesafenin noktaya göre değişimi küçük → çizgi mantıklı yerden çıkar,
      // değer ihmal edilebilir farkla aynı kalır. Gerçekten açılı (>2°) yüzeyde en-yakın nokta korunur.
      if (_vrsylModlar && yuzey1Duzlem && yuzey2Duzlem && derece < 2 && duzlem1 && duzlem2) {
        const baz1 = (cizgiNokta1 && cizgiNokta1.lengthSq() > 0) ? cizgiNokta1.clone() : merkez1.clone();
        const ayak2 = new THREE.Vector3();
        duzlem2.projectPoint(baz1, ayak2);
        olcumNokta1 = baz1;
        olcumNokta2 = ayak2;
      }

      // 🎯 ÖLÇÜM MODU: Mesafeyi yeniden hesapla
      const kisaDuzAktif = secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz';
      const modAktif = secim1OlcumModu !== 'enKisa' && secim1OlcumModu !== 'enKisaDuz' || secim2OlcumModu !== 'enKisa' && secim2OlcumModu !== 'enKisaDuz';
      let gosterilecekMesafe: number;

      // 🎯 KISA DÜZ: Her zaman dik (perpendicular) mesafe kullan - CATIA style
      if (kisaDuzAktif) {
        // Dik mesafe kullan (zaten hesaplanmış enKisaDikMesafe)
        if (yuzey1Duzlem && duzlem1) {
          gosterilecekMesafe = Math.abs(duzlem1.distanceToPoint(olcumNokta2));
        } else if (yuzey2Duzlem && duzlem2) {
          gosterilecekMesafe = Math.abs(duzlem2.distanceToPoint(olcumNokta1));
        } else {
          gosterilecekMesafe = enKisaDikMesafe;
        }
      } else if (modAktif) {
        // En Uzak veya Merkez modunda: düzlem varsa dik mesafe, yoksa nokta-nokta mesafesi
        if ((yuzey1Duzlem || yuzey2Duzlem) && (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak')) {
          // Dik mesafe hesapla (CATIA style)
          if (yuzey1Duzlem && duzlem1) {
            gosterilecekMesafe = Math.abs(duzlem1.distanceToPoint(olcumNokta2));
          } else if (yuzey2Duzlem && duzlem2) {
            gosterilecekMesafe = Math.abs(duzlem2.distanceToPoint(olcumNokta1));
          } else {
            gosterilecekMesafe = olcumNokta1.distanceTo(olcumNokta2);
          }
        } else {
          gosterilecekMesafe = olcumNokta1.distanceTo(olcumNokta2);
        }
      } else {
        gosterilecekMesafe = enKisaDikMesafe;
      }

      // Sonuç nesnesindeki mesafeyi güncelle
      if (sonuc?.mesafe) {
        sonuc.mesafe.olcu = gosterilecekMesafe;
        sonuc.mesafe.toleransMin = gosterilecekMesafe - tolerans;
        sonuc.mesafe.toleransMax = gosterilecekMesafe + tolerans;
        // Açıklama güncelle
        if (secim1OlcumModu === 'merkez' || secim2OlcumModu === 'merkez') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Merkeze');
        } else if (secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Kısa Düz');
        } else if (secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Uzak Düz');
        } else if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Uzak');
        } else if (secim1OlcumModu === 'enKisa' || secim2OlcumModu === 'enKisa') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Kısa');
        }
      }

      // ✅ Arrow line çiz - ÖLÇÜM MODU'NA GÖRE
      const mesafedonusmus = this.birimDonustur(gosterilecekMesafe, 'mm', secilenBirim, birimFaktore);

      // 🎯 PARALEL DEĞİL UYARISI: Yüzeyler paralel değilse uyarı işareti ekle
      // Tolerans: 0.001 dereceden büyük açı = paralel değil uyarısı göster
      const paralelDegil = derece > 0.001;
      const paralelUyarisi = paralelDegil ? ' ⚠∠' : '';

      let labelMetin = derece > 0.001 ? `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim} (${aci.toFixed(aciBasamak)}°)${paralelUyarisi}` : `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim}`;

      // 🎯 Ölçüm modu göstergesini label'a ekle
      if (secim1OlcumModu === 'enKisaDuz') labelMetin += ' [⊥1]';
      else if (secim1OlcumModu === 'enKisa') labelMetin += ' [↔1]';
      else if (secim1OlcumModu === 'merkez') labelMetin += ' [⊙1]';
      else if (secim1OlcumModu === 'enUzak') labelMetin += ' [⤢1]';
      else if (secim1OlcumModu === 'enUzakDuz') labelMetin += ' [⇕1]';
      if (secim2OlcumModu === 'enKisaDuz') labelMetin += ' [⊥2]';
      else if (secim2OlcumModu === 'enKisa') labelMetin += ' [↔2]';
      else if (secim2OlcumModu === 'merkez') labelMetin += ' [⊙2]';
      else if (secim2OlcumModu === 'enUzak') labelMetin += ' [⤢2]';
      else if (secim2OlcumModu === 'enUzakDuz') labelMetin += ' [⇕2]';

      // 🔵 Merkez bilgisini label'a ekle (en kısa modundayken)
      if (secim1OlcumModu === 'enKisa' && yuzey1Silindir) {
        const r = this.birimDonustur(yuzey1Silindir.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | ${yuzey1Silindir.tip === 'silindir' ? 'Sil1' : 'Küre1'} R:${r.toFixed(mesafeBas)}`;
      }
      if (secim2OlcumModu === 'enKisa' && yuzey2Silindir) {
        const r = this.birimDonustur(yuzey2Silindir.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | ${yuzey2Silindir.tip === 'silindir' ? 'Sil2' : 'Küre2'} R:${r.toFixed(mesafeBas)}`;
      }

      gosterimKayniyaEkleCallback(olcumNokta1, olcumNokta2, labelMetin, false, 0);

      // 🔴 İKİNCİ ÇİZGİ KALDIRILDI: Merkeze ölçüm çizgisi artık çizilmiyor
      // Kullanıcı talebi: Sadece ana ölçüm çizgisi gösterilsin

      detectChangesCallback();
    }

    /** NOT: Nokta - Yüzey Mesafesi */
    if (efektifTip1 == "Nokta" && efektifTip2 == "Yüzey") {
      let nokta = new THREE.Vector3();

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta1) {
        nokta = merkezNokta1.clone();
      }
      // Handle Points selection (prefer actual vertex over intersection point)
      else if (secilen1.object?.type === "Points" || secilen1.altObje?.type === "Points") {
        const f = (secilen1.altObje || secilen1.object) as THREE.Points;
        const pointIndex = secilen1PointIndex !== null ? secilen1PointIndex : secilen1.index;
        nokta = new FikoVektor3D(
          f.geometry.attributes["position"].getX(pointIndex),
          f.geometry.attributes["position"].getY(pointIndex),
          f.geometry.attributes["position"].getZ(pointIndex)
        );

        if (f.matrixWorld) {
          nokta.applyMatrix4(f.matrixWorld);
        } else if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4((f.parent as any).matrixWorld);
        }
      } else {
        nokta = secilen1.point?.clone() || new THREE.Vector3();
      }

      // Orijinal yüzey mesh'ini / FikoCizimYalin'i al
      // secilen2.object = highlight mesh
      // secilen2.altObje = FikoCizimYalin (true source)
      let geom: THREE.BufferGeometry | null = null;

      // Eğer altObje (FikoCizimYalin) var ise, meshGeom'u kullan
      if (secilen2?.altObje && (secilen2.altObje as any).meshGeom) {
        geom = (secilen2.altObje as any).meshGeom.clone();
      } else if (secilen2?.object && (secilen2.object as any).meshGeom) {
        geom = (secilen2.object as any).meshGeom.clone();
      } else {
        // Fallback: highlight mesh'in geometry'si
        const yuzey = secilen2?.object as THREE.Mesh;
        if (yuzey && yuzey.geometry) {
          geom = yuzey.geometry.clone();
        }
      }

      if (!geom) {
        return null;
      }

      // 🎯 DÜZELTME: Yüzey geometrisini WORLD KOORDİNATLARINA dönüştür
      const yuzeyMesh = (secilen2?.altObje || secilen2?.object) as THREE.Mesh;
      if (yuzeyMesh && yuzeyMesh.matrixWorld) {
        geom.applyMatrix4(yuzeyMesh.matrixWorld);
      }

      // Seçilen yüzey'in face index'ini al (intersection point'in hangi face'de olduğu)
      const faceIndex = secilen2.faceIndex ?? -1;

      // Yüzeye dikey olan mesafeyi hesapla (seçili face'e bakarak)
      const mesafeProje = this.geomService.noktaYuzeyMesafeVeProje(nokta, geom, faceIndex);
      const mesafe = mesafeProje.mesafe;
      const proyeNokta = mesafeProje.projeNokta;

      // Geçmişe ekle
      olcumGecmisineEkleCallback("Uzaklık", mesafe, "mm", "Nokta-Yüzey");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Uzaklık");

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Uzaklık",
          olcu: mesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: mesafe - tolerans,
          toleransMax: mesafe + tolerans
        }
      };

      // Arrow line çiz - nokta ile yüzey projesi arasında
      const mesafedonusmus = this.birimDonustur(mesafe, 'mm', secilenBirim, birimFaktore);
      gosterimKayniyaEkleCallback(nokta, proyeNokta, mesafedonusmus.toFixed(mesafeBas) + ' ' + secilenBirim);

      detectChangesCallback();
    }

    /** NOT: Yüzey - Nokta Mesafesi */
    if (efektifTip1 == "Yüzey" && efektifTip2 == "Nokta") {

      // Orijinal yüzey mesh'ini / FikoCizimYalin'i al
      // secilen1.object = highlight mesh
      // secilen1.altObje = FikoCizimYalin (true source)
      let geom: THREE.BufferGeometry | null = null;

      // Eğer altObje (FikoCizimYalin) var ise, meshGeom'u kullan
      if (secilen1?.altObje && (secilen1.altObje as any).meshGeom) {
        geom = (secilen1.altObje as any).meshGeom.clone();
      } else if (secilen1?.object && (secilen1.object as any).meshGeom) {
        geom = (secilen1.object as any).meshGeom.clone();
      } else {
        // Fallback: highlight mesh'in geometry'si
        const yuzey = secilen1?.object as THREE.Mesh;
        if (yuzey && yuzey.geometry) {
          geom = yuzey.geometry.clone();
        }
      }

      let nokta = new THREE.Vector3();

      // 🎯 MERKEZ MODU: Merkez noktası varsa onu kullan
      if (merkezNokta2) {
        nokta = merkezNokta2.clone();
      }
      // Handle Points selection (prefer actual vertex over intersection point)
      else if (secilen2.object?.type === "Points" || secilen2.altObje?.type === "Points") {
        const f = (secilen2.altObje || secilen2.object) as THREE.Points;
        const pointIndex = secilen2PointIndex !== null ? secilen2PointIndex : secilen2.index;
        nokta = new FikoVektor3D(
          f.geometry.attributes["position"].getX(pointIndex),
          f.geometry.attributes["position"].getY(pointIndex),
          f.geometry.attributes["position"].getZ(pointIndex)
        );
        if (f.matrixWorld) {
          nokta.applyMatrix4(f.matrixWorld);
        } else if (f.parent && (f.parent as any).matrixWorld) {
          nokta.applyMatrix4((f.parent as any).matrixWorld);
        }
      } else {
        nokta = secilen2.point?.clone() || new THREE.Vector3();
      }

      if (!geom) {
        return null;
      }

      // 🎯 DÜZELTME: Yüzey geometrisini WORLD KOORDİNATLARINA dönüştür
      const yuzeyMesh = (secilen1?.altObje || secilen1?.object) as THREE.Mesh;
      if (yuzeyMesh && yuzeyMesh.matrixWorld) {
        geom.applyMatrix4(yuzeyMesh.matrixWorld);
      }

      // Seçilen yüzey'in face index'ini al (intersection point'in hangi face'de olduğu)
      const faceIndex = secilen1.faceIndex ?? -1;

      // Yüzeye dikey olan mesafeyi hesapla (seçili face'e bakarak)
      const mesafeProje = this.geomService.noktaYuzeyMesafeVeProje(nokta, geom, faceIndex);
      const mesafe = mesafeProje.mesafe;
      const proyeNokta = mesafeProje.projeNokta;

      // Geçmişe ekle
      olcumGecmisineEkleCallback("Uzaklık", mesafe, "mm", "Yüzey-Nokta");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Uzaklık");

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Uzaklık",
          olcu: mesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: mesafe - tolerans,
          toleransMax: mesafe + tolerans
        }
      };

      // Arrow line çiz - yüzey projesi ile nokta arasında
      const mesafedonusmus = this.birimDonustur(mesafe, 'mm', secilenBirim, birimFaktore);
      gosterimKayniyaEkleCallback(proyeNokta, nokta, mesafedonusmus.toFixed(mesafeBas) + ' ' + secilenBirim);

      detectChangesCallback();
    }

    /** NOT: Çizgi - Çizgi Mesafesi */
    if (efektifTip1 == "Çizgi" && efektifTip2 == "Çizgi") {

      let cizgi1Geom = new THREE.BufferGeometry();
      let cizgi2Geom = new THREE.BufferGeometry();

      // Yedeklenen geometrileri kullan
      cizgi1Geom = secilen1Geom;
      cizgi2Geom = secilen2Geom;

      if (!cizgi1Geom || !cizgi2Geom) {
        return null;
      }

      // Çizgilerin position attribute'larını al
      const pos1 = cizgi1Geom.attributes.position;
      const pos2 = cizgi2Geom.attributes.position;

      if (!pos1 || !pos2 || pos1.count < 2 || pos2.count < 2) {
        return null;
      }

      // 🔵 ÇEMBER TESPİTİ - Merkez ve yarıçap bilgisi
      const cizgi1Cember = this.cizgiCemberTespit(cizgi1Geom);
      const cizgi2Cember = this.cizgiCemberTespit(cizgi2Geom);

      // 🎯 DÜZELTME: secilen1Geom ve secilen2Geom zaten WORLD SPACE'de!
      // cizgiGeometriAlVeTransformUygula() fonksiyonu matrixWorld'u zaten uyguladı
      // Bu yüzden tekrar transform UYGULANMAMALI - çift dönüşüm hatası!

      // 🎯 TÜM GEOMETRİ NOKTALARINI AL (zaten WORLD SPACE'de)
      const cizgi1Noktalar: THREE.Vector3[] = [];
      const cizgi2Noktalar: THREE.Vector3[] = [];

      for (let i = 0; i < pos1.count; i++) {
        const nokta = new THREE.Vector3(pos1.getX(i), pos1.getY(i), pos1.getZ(i));
        // 🎯 DÜZELTME: Transform UYGULANMIYOR - geometri zaten world space'de
        cizgi1Noktalar.push(nokta);
      }

      for (let i = 0; i < pos2.count; i++) {
        const nokta = new THREE.Vector3(pos2.getX(i), pos2.getY(i), pos2.getZ(i));
        // 🎯 DÜZELTME: Transform UYGULANMIYOR - geometri zaten world space'de
        cizgi2Noktalar.push(nokta);
      }

      // Uç noktalar (yön hesabı için)
      const p1Start = cizgi1Noktalar[0];
      const p1End = cizgi1Noktalar[cizgi1Noktalar.length - 1];
      const p2Start = cizgi2Noktalar[0];
      const p2End = cizgi2Noktalar[cizgi2Noktalar.length - 1];

      // Çizgi yön vektörlerini hesapla (düz çizgiler için)
      const dir1 = new THREE.Vector3().subVectors(p1End, p1Start).normalize();
      const dir2 = new THREE.Vector3().subVectors(p2End, p2Start).normalize();

      // Çizgiler arası açıyı hesapla
      const dotProduct = Math.abs(dir1.dot(dir2));
      const radiyan = Math.acos(Math.min(1, dotProduct));
      const derece = (radiyan * 180) / Math.PI;

      // 🎯 PARALEL ÇİZGİLER İÇİN PERPENDİKÜLER MESAFE HESAPLA
      // Çapraz ölçüm yerine dik mesafe kullan
      let enKisaMesafe = Infinity;
      let enYakinNokta1 = new THREE.Vector3();
      let enYakinNokta2 = new THREE.Vector3();
      let enUzakMesafe = 0;
      let enUzakNokta1 = new THREE.Vector3();
      let enUzakNokta2 = new THREE.Vector3();

      // Paralel çizgi kontrolü (açı < 1 derece = paralel kabul)
      const paralelMi = derece < 1.0;

      if (paralelMi) {
        // 🎯 PARALEL ÇİZGİLER - Perpendicular (dik) mesafe hesapla
        // Çizgi1'in yönünü kullanarak çizgi2'deki noktaların dik mesafesini hesapla
        const line1 = new THREE.Line3(p1Start, p1End);

        // Her çizgi2 noktasından çizgi1'e dik mesafe hesapla
        let minPerpDist = Infinity;
        let maxPerpDist = 0;
        const tempClosest = new THREE.Vector3();

        for (const nokta2 of cizgi2Noktalar) {
          // Çizgi1 üzerinde nokta2'ye en yakın noktayı bul
          line1.closestPointToPoint(nokta2, false, tempClosest); // clampToLine = false (sonsuz çizgi)
          const perpDist = nokta2.distanceTo(tempClosest);

          if (perpDist < minPerpDist) {
            minPerpDist = perpDist;
            enYakinNokta2 = nokta2.clone();
            enYakinNokta1 = tempClosest.clone();
          }
          if (perpDist > maxPerpDist) {
            maxPerpDist = perpDist;
            enUzakNokta2 = nokta2.clone();
            enUzakNokta1 = tempClosest.clone();
          }
        }

        // Aynısını çizgi1 noktaları için de yap (daha iyi sonuç için)
        const line2 = new THREE.Line3(p2Start, p2End);
        for (const nokta1 of cizgi1Noktalar) {
          line2.closestPointToPoint(nokta1, false, tempClosest);
          const perpDist = nokta1.distanceTo(tempClosest);

          if (perpDist < minPerpDist) {
            minPerpDist = perpDist;
            enYakinNokta1 = nokta1.clone();
            enYakinNokta2 = tempClosest.clone();
          }
          if (perpDist > maxPerpDist) {
            maxPerpDist = perpDist;
            enUzakNokta1 = nokta1.clone();
            enUzakNokta2 = tempClosest.clone();
          }
        }

        enKisaMesafe = minPerpDist;
        enUzakMesafe = maxPerpDist;
      } else {
        // 🎯 PARALEL OLMAYAN ÇİZGİLER - Vertex-vertex brute force
        // Tüm nokta çiftlerini kontrol et
        for (const nokta1 of cizgi1Noktalar) {
          for (const nokta2 of cizgi2Noktalar) {
            const mesafeTemp = nokta1.distanceTo(nokta2);

            // En kısa
            if (mesafeTemp < enKisaMesafe) {
              enKisaMesafe = mesafeTemp;
              enYakinNokta1 = nokta1.clone();
              enYakinNokta2 = nokta2.clone();
            }

            // En uzak
            if (mesafeTemp > enUzakMesafe) {
              enUzakMesafe = mesafeTemp;
              enUzakNokta1 = nokta1.clone();
              enUzakNokta2 = nokta2.clone();
            }
          }
        }
      }

      // 🎯 EN KISA ve EN UZAK: Geometri vertex'lerinden alınıyor (yukarıdaki loop'tan)
      // Matematiksel formül KULLANILMIYOR - kullanıcı talebi

      // 🎯 ÖLÇÜM MODU: Ölçüm noktalarını belirle
      let olcumNokta1 = enYakinNokta1.clone();
      let olcumNokta2 = enYakinNokta2.clone();

      // 🎯 KISA DÜZ MODU: Herhangi biri enKisaDuz ise, dik projeksiyon ile en kısa mesafe
      const kisaDuzAktif = secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz';

      // 🎯 UZAK DÜZ MODU: Herhangi biri enUzakDuz ise, her ikisi için de düz hesapla
      const uzakDuzAktif = secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz';

      // 🎯 KISA DÜZ - Geometriden projeksiyon ile en kısa düz mesafe (CATIA style)
      if (kisaDuzAktif && secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
        const merkez1 = secim1MerkezBilgisi.merkez;
        const merkez2 = secim2MerkezBilgisi.merkez;
        const merkezYon = new THREE.Vector3().subVectors(merkez2, merkez1).normalize();

        // Geometri noktalarından düz projeksiyon ile bul (matematiksel formül YOK)
        let enYakinProj1 = -Infinity;
        let enYakinProj2 = -Infinity;

        // Çizgi 1'den merkez2'ye en yakın nokta (yön boyunca en ileri)
        for (const nokta of cizgi1Noktalar) {
          const proj = nokta.clone().sub(merkez1).dot(merkezYon);
          if (proj > enYakinProj1) {
            enYakinProj1 = proj;
            olcumNokta1 = nokta.clone();
          }
        }

        // Çizgi 2'den merkez1'e en yakın nokta (ters yön boyunca en ileri)
        for (const nokta of cizgi2Noktalar) {
          const proj = nokta.clone().sub(merkez2).dot(merkezYon.clone().negate());
          if (proj > enYakinProj2) {
            enYakinProj2 = proj;
            olcumNokta2 = nokta.clone();
          }
        }
      }
      else if (uzakDuzAktif && secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
        // 🎯 UZAK DÜZ - Geometriden projeksiyon ile en uzak düz noktaları bul (matematiksel formül YOK)
        const merkez1 = secim1MerkezBilgisi.merkez;
        const merkez2 = secim2MerkezBilgisi.merkez;
        const merkezYon = new THREE.Vector3().subVectors(merkez2, merkez1).normalize();

        let enUzakProj1 = -Infinity;
        let enUzakProj2 = -Infinity;

        // Çizgi 1'den merkez2'ye en uzak nokta (ters yön boyunca en ileri)
        for (const nokta of cizgi1Noktalar) {
          const proj = nokta.clone().sub(merkez1).dot(merkezYon.clone().negate());
          if (proj > enUzakProj1) {
            enUzakProj1 = proj;
            olcumNokta1 = nokta.clone();
          }
        }

        // Çizgi 2'den merkez1'e en uzak nokta (yön boyunca en ileri)
        for (const nokta of cizgi2Noktalar) {
          const proj = nokta.clone().sub(merkez2).dot(merkezYon);
          if (proj > enUzakProj2) {
            enUzakProj2 = proj;
            olcumNokta2 = nokta.clone();
          }
        }
      } else {
        // Normal modlar (enKisa, merkez, enUzak)
        // 🎯 ÖNEMLİ: Karışık modlarda (biri merkez, diğeri en kısa) doğru hesaplama yap

        // Önce her iki seçimin hedef noktasını belirle
        let hedef1: THREE.Vector3 | null = null; // Seçim1'in hedefi (seçim2'nin konumu)
        let hedef2: THREE.Vector3 | null = null; // Seçim2'nin hedefi (seçim1'in konumu)

        // Seçim 1 için mod kontrolü
        if (secim1OlcumModu === 'merkez' && secim1MerkezBilgisi?.merkez) {
          olcumNokta1 = secim1MerkezBilgisi.merkez.clone();
          hedef2 = olcumNokta1.clone(); // Seçim2, Seçim1'in merkezine göre hesaplanacak
        } else if (secim1OlcumModu === 'enUzak') {
          olcumNokta1 = enUzakNokta1.clone();
          hedef2 = olcumNokta1.clone();
        }
        // enKisa için hedef2 sonra belirlenir

        // Seçim 2 için mod kontrolü
        if (secim2OlcumModu === 'merkez' && secim2MerkezBilgisi?.merkez) {
          olcumNokta2 = secim2MerkezBilgisi.merkez.clone();
          hedef1 = olcumNokta2.clone(); // Seçim1, Seçim2'nin merkezine göre hesaplanacak
        } else if (secim2OlcumModu === 'enUzak') {
          olcumNokta2 = enUzakNokta2.clone();
          hedef1 = olcumNokta2.clone();
        }
        // enKisa için hedef1 sonra belirlenir

        // 🎯 KARISIK MOD DÜZELTMESİ: Biri merkez/enUzak, diğeri enKisa ise
        // En kısa modundaki seçim, diğerinin konumuna göre yeniden hesaplanmalı

        // Seçim1 enKisa ve hedef1 varsa (Seçim2 merkez veya enUzak)
        if (secim1OlcumModu === 'enKisa' && hedef1) {
          // Geometri noktalarından hedef1'e en yakını bul (matematiksel formül YOK)
          let minDist = Infinity;
          for (const nokta of cizgi1Noktalar) {
            const dist = nokta.distanceTo(hedef1);
            if (dist < minDist) {
              minDist = dist;
              olcumNokta1 = nokta.clone();
            }
          }
        }

        // Seçim2 enKisa ve hedef2 varsa (Seçim1 merkez veya enUzak)
        if (secim2OlcumModu === 'enKisa' && hedef2) {
          // Geometri noktalarından hedef2'ye en yakını bul (matematiksel formül YOK)
          let minDist = Infinity;
          for (const nokta of cizgi2Noktalar) {
            const dist = nokta.distanceTo(hedef2);
            if (dist < minDist) {
              minDist = dist;
              olcumNokta2 = nokta.clone();
            }
          }
        }

        // Her ikisi de enKisa ise önceden hesaplanan değerler kullanılır (zaten atanmış)
      }

      // Gösterilecek mesafeyi hesapla
      const gosterilecekMesafe = olcumNokta1.distanceTo(olcumNokta2);

      // Mesafe veya açı göster
      if (derece < 0.001 || cizgi1Cember || cizgi2Cember) {
        // PARALEL ÇIZGILER veya EĞRİ ÇİZGİLER - MESAFE GÖSTER
        // Geçmişe ekle
        let aciklama = "Çizgiler Arası Mesafe";
        if (secim1OlcumModu === 'merkez' || secim2OlcumModu === 'merkez') {
          aciklama = "Çizgiler Arası Mesafe (Merkeze)";
        } else if (secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz') {
          aciklama = "Çizgiler Arası Mesafe (Kısa Düz)";
        } else if (secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
          aciklama = "Çizgiler Arası Mesafe (Uzak Düz)";
        } else if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak') {
          aciklama = "Çizgiler Arası Mesafe (En Uzak)";
        } else if (secim1OlcumModu === 'enKisa' || secim2OlcumModu === 'enKisa') {
          aciklama = "Çizgiler Arası Mesafe (En Kısa)";
        } else {
          aciklama = "Çizgiler Arası Mesafe (Kısa Düz)";
        }

        olcumGecmisineEkleCallback(aciklama, gosterilecekMesafe, "mm", "Çizgi-Çizgi");

        // Min/Max/Ortalama hesapla
        const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, aciklama);

        sonuc = {
          turu: 'Mesafe',
          mesafe: {
            aciklama: aciklama,
            olcu: gosterilecekMesafe,
            birim: "mm",
            min: minMaxOrta?.min,
            max: minMaxOrta?.max,
            ortalama: minMaxOrta?.ortalama,
            toleransMin: gosterilecekMesafe - tolerans,
            toleransMax: gosterilecekMesafe + tolerans,
            // 🔵 Merkez bilgileri
            cizgi1Merkez: cizgi1Cember?.merkez,
            cizgi1Yaricap: cizgi1Cember?.yaricap,
            cizgi2Merkez: cizgi2Cember?.merkez,
            cizgi2Yaricap: cizgi2Cember?.yaricap
          }
        };

        // Arrow line çiz - ÖLÇÜM MODU'NA GÖRE
        const mesafedonusmus = this.birimDonustur(gosterilecekMesafe, 'mm', secilenBirim, birimFaktore);
        let labelMetin = `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim}`;

        // 🎯 Ölçüm modu göstergesini label'a ekle
        if (secim1OlcumModu === 'enKisaDuz') labelMetin += ' [⊥1]';
        else if (secim1OlcumModu === 'enKisa') labelMetin += ' [↔1]';
        else if (secim1OlcumModu === 'merkez') labelMetin += ' [⊙1]';
        else if (secim1OlcumModu === 'enUzak') labelMetin += ' [⤢1]';
        else if (secim1OlcumModu === 'enUzakDuz') labelMetin += ' [⇕1]';
        if (secim2OlcumModu === 'enKisaDuz') labelMetin += ' [⊥2]';
        else if (secim2OlcumModu === 'enKisa') labelMetin += ' [↔2]';
        else if (secim2OlcumModu === 'merkez') labelMetin += ' [⊙2]';
        else if (secim2OlcumModu === 'enUzak') labelMetin += ' [⤢2]';
        else if (secim2OlcumModu === 'enUzakDuz') labelMetin += ' [⇕2]';

        // 🔵 Merkez bilgisini label'a ekle (en kısa modundayken)
        if (secim1OlcumModu === 'enKisa' && cizgi1Cember) {
          const r = this.birimDonustur(cizgi1Cember.yaricap, 'mm', secilenBirim, birimFaktore);
          labelMetin += ` | Çizgi1 R:${r.toFixed(mesafeBas)}`;
        }
        if (secim2OlcumModu === 'enKisa' && cizgi2Cember) {
          const r = this.birimDonustur(cizgi2Cember.yaricap, 'mm', secilenBirim, birimFaktore);
          labelMetin += ` | Çizgi2 R:${r.toFixed(mesafeBas)}`;
        }

        gosterimKayniyaEkleCallback(olcumNokta1, olcumNokta2, labelMetin, false, 0);
      } else {
        // AÇILI DÜZGÜN ÇIZGILER - AÇI VE EN KISA MESAFE GÖSTER
        const aci = Math.min(derece, 180 - derece);

        // 🎯 En kısa mesafeyi de hesapla (skew lines - aykırı çizgiler için)
        // İki doğru arasındaki en kısa mesafe formülü: |(P2-P1) · (D1 x D2)| / |D1 x D2|
        const crossDir = new THREE.Vector3().crossVectors(dir1, dir2);
        const crossDirLen = crossDir.length();

        let enKisaSkewMesafe = 0;
        if (crossDirLen > 0.0001) {
          // Aykırı çizgiler - matematiksel en kısa mesafe
          const p2MinusP1 = new THREE.Vector3().subVectors(p2Start, p1Start);
          enKisaSkewMesafe = Math.abs(p2MinusP1.dot(crossDir)) / crossDirLen;
        } else {
          // Neredeyse paralel - vertex mesafesi kullan
          enKisaSkewMesafe = enKisaMesafe;
        }

        // Geçmişe ekle - Açı ve mesafe birlikte
        olcumGecmisineEkleCallback("Çizgiler Arası Açı", aci, "°", "Çizgi-Çizgi (Açı)");
        olcumGecmisineEkleCallback("Çizgiler Arası Mesafe", enKisaSkewMesafe, "mm", "Çizgi-Çizgi (Mesafe)");

        // Min/Max/Ortalama hesapla
        const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Çizgiler Arası Açı");

        // 🎯 Sonuçta hem açı hem mesafe bilgisi
        sonuc = {
          turu: 'Mesafe',
          mesafe: {
            aciklama: "Çizgiler Arası Açı ve Mesafe",
            olcu: aci,
            birim: "°",
            min: minMaxOrta?.min,
            max: minMaxOrta?.max,
            ortalama: minMaxOrta?.ortalama,
            toleransMin: aci - tolerans,
            toleransMax: aci + tolerans,
            // 🎯 En kısa mesafe bilgisi eklendi
            enKisaMesafe: enKisaSkewMesafe
          }
        };

        // ✅ Arrow line çiz - Açı ve mesafe birlikte göster
        const mesafedonusmus = this.birimDonustur(enKisaSkewMesafe, 'mm', secilenBirim, birimFaktore);
        const labelMetin = `${aci.toFixed(aciBasamak)}° | ${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim}`;
        const arcMerkezi = new THREE.Vector3().lerpVectors(olcumNokta1, olcumNokta2, 0.5);
        gosterimKayniyaEkleCallback(olcumNokta1, olcumNokta2, labelMetin, true, aci, arcMerkezi);
      }

      detectChangesCallback();
    }

    /** NOT: Yüzey - Çizgi Mesafesi */
    if (efektifTip1 == "Yüzey" && efektifTip2 == "Çizgi") {

      let yuzey: THREE.Mesh = (secilen1?.altObje || secilen1?.object) as THREE.Mesh;
      let cizgi: THREE.LineSegments = (secilen2?.altObje || secilen2?.object) as THREE.LineSegments;

      if (!yuzey || !cizgi) {
        return null;
      }

      // Yedeklenen geometrileri al ve CLONE'la
      // ÖNEMLI: Yüzey için MESH'İN TAM GEOMETRİSİNİ kullan (normal hesabı için)
      // Highlight sadece tek face, açı hesabı için yetersiz
      // Çizgi için normal geometri kullan (zaten tek çizgi)
      let cizgiGeom = secilen2Geom?.clone();
      let yuzeyGeom = secilen1Geom?.clone(); // Mesh'in TAM geometrisi

      if (!cizgiGeom) {
        return null;
      }

      if (!yuzeyGeom) {
        return null;
      }

      // YÜZEY GEOMETRİSİNİ WORLD KOORDİNATLARINA ÇEVİR
      // Highlight geometrisi local koordinatlarda, yüzey objesinin transform'unu uygula
      if (yuzey && yuzey.matrixWorld) {
        yuzeyGeom.applyMatrix4(yuzey.matrixWorld);
      }

      // 🎯 DÜZELTME: ÇİZGİ GEOMETRİSİ ZATEN WORLD SPACE'DE!
      // secilen2Geom, cizgiGeometriAlVeTransformUygula() tarafından zaten dönüştürüldü
      // Tekrar transform UYGULANMAMALI - çift dönüşüm hatası!
      // (Eski kod kaldırıldı)

      // GEOMETRİK EN KISA MESAFE: Yüzey ve çizgi arasındaki gerçek en kısa mesafe
      // Tüm yüzey vertexlerinden çizgiye olan mesafeleri kontrol et
      const yuzeyPositions = yuzeyGeom.attributes.position;
      let geometrikEnKisaMesafe = Infinity;
      let geometrikYakınNoktaYuzey = new THREE.Vector3();
      let geometrikYakınNoktaCizgi = new THREE.Vector3();

      for (let i = 0; i < yuzeyPositions.count; i++) {
        const vertex = new THREE.Vector3(
          yuzeyPositions.getX(i),
          yuzeyPositions.getY(i),
          yuzeyPositions.getZ(i)
        );
        const projeNokta = this.geomService.alCizgiProjeNokta(cizgiGeom, vertex);
        if (projeNokta.mesafe < geometrikEnKisaMesafe) {
          geometrikEnKisaMesafe = projeNokta.mesafe;
          geometrikYakınNoktaYuzey = vertex.clone();
          geometrikYakınNoktaCizgi = projeNokta.nokta.clone();
        }
      }

      // TIKLAMA MESAFESI: Tıklanan noktadan çizgiye olan mesafe (referans için)
      const yakınNoktaYuzey = secilen1.point.clone();
      const tiklamaProje = this.geomService.alCizgiProjeNokta(cizgiGeom, yakınNoktaYuzey);
      const tiklamaMesafe = tiklamaProje.mesafe;

      // Çizginin yön vektörünü hesapla (transform'lanmış geometriden)
      let cizgiStart = new THREE.Vector3();
      let cizgiEnd = new THREE.Vector3();
      const cizgiPos = cizgiGeom.attributes.position;

      if (cizgiPos.count >= 2) {
        cizgiStart = new THREE.Vector3(cizgiPos.getX(0), cizgiPos.getY(0), cizgiPos.getZ(0));
        cizgiEnd = new THREE.Vector3(cizgiPos.getX(cizgiPos.count - 1), cizgiPos.getY(cizgiPos.count - 1), cizgiPos.getZ(cizgiPos.count - 1));
      }
      const cizgiDir = new THREE.Vector3().subVectors(cizgiEnd, cizgiStart).normalize();

      // Tıklanan face'in normalını al
      // 🎯 DÜZELTME: face.normal LOCAL space'de gelir, WORLD space'e dönüştür!
      let yuzeyNormal: THREE.Vector3;
      if (secilen1.face && secilen1.face.normal) {
        // Normal'ı world space'e dönüştür (normalMatrix kullan)
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(yuzey.matrixWorld);
        yuzeyNormal = secilen1.face.normal.clone().applyMatrix3(normalMatrix).normalize();
      } else {
        // Fallback: Highlight geometrisinden hesapla
        const highlightGeom = fikoSecim1.sonSecilenHighlightGeometriYuzey?.clone();
        if (highlightGeom && yuzey.matrixWorld) {
          highlightGeom.applyMatrix4(yuzey.matrixWorld);
          yuzeyNormal = this.geomService.alYuzeyNormal(highlightGeom);
        } else {
          return null;
        }
      }

      // Çizgi yönü ile yüzey normalı arasındaki açıyı hesapla
      const dotProduct = Math.abs(cizgiDir.dot(yuzeyNormal));
      const radiyan = Math.acos(Math.min(1, dotProduct));
      const normalArasiAci = (radiyan * 180) / Math.PI;

      // Çizgi-Yüzey açısı: Çizgi yüzeyde ne kadar açılı
      // Normal ile çizgi arası 90° ise çizgi yüzeyde paralel (açı = 0)
      // Normal ile çizgi arası 0° ise çizgi yüzeyde dik (açı = 90)
      const cizgiYuzeyAcisi = Math.abs(90 - normalArasiAci);

      // 🎯 HER ZAMAN DİK MESAFE ÖLÇÜMÜ - EN KISA DİK MESAFE (düzlem seçildiğinde)
      // Çizginin tüm noktalarından yüzey düzlemine dik mesafe hesapla, en kısa olanı bul

      // Yüzey düzlemini oluştur (tıklanan noktadan geçen, normal'i yuzeyNormal olan)
      const yuzeyDuzlem = new THREE.Plane();
      const yuzeyNoktasi = secilen1.point.clone();
      yuzeyDuzlem.setFromNormalAndCoplanarPoint(yuzeyNormal, yuzeyNoktasi);

      // Çizginin tüm noktalarından yüzeye dik mesafe hesapla - 🚀 GPU ACCELERATED
      let enKisaDikMesafe = Infinity;
      let enYakinCizgiNoktasi = new THREE.Vector3();
      let enYakinDikProjeksiyonNoktasi = new THREE.Vector3();

      try {
        // GPU ile hesapla
        const gpuResult = await this.geomService.cizgiDuzlemMesafeHesapla(cizgiGeom, yuzeyNormal, yuzeyNoktasi);
        enKisaDikMesafe = gpuResult.mesafe;
        enYakinCizgiNoktasi = gpuResult.enYakinNokta;
        enYakinDikProjeksiyonNoktasi = gpuResult.projeksiyon;
      } catch (e) {
        // CPU Fallback
        const cizgiPositions = cizgiGeom.attributes.position;
        for (let i = 0; i < cizgiPositions.count; i++) {
          const cizgiVertex = new THREE.Vector3(
            cizgiPositions.getX(i),
            cizgiPositions.getY(i),
            cizgiPositions.getZ(i)
          );

          // Bu noktadan yüzeye dik mesafe
          const dikMesafeTemp = Math.abs(yuzeyDuzlem.distanceToPoint(cizgiVertex));

          if (dikMesafeTemp < enKisaDikMesafe) {
            enKisaDikMesafe = dikMesafeTemp;
            enYakinCizgiNoktasi = cizgiVertex.clone();
            // Projeksiyon noktası
            yuzeyDuzlem.projectPoint(cizgiVertex, enYakinDikProjeksiyonNoktasi);
          }
        }
      }

      const dikMesafe = enKisaDikMesafe;
      const dikProjeksiyonNokta = enYakinDikProjeksiyonNoktasi;

      // 🎯 EN UZAK MESAFE: Geometriden gerçek en uzak noktaları bul (CATIA style)
      let enUzakMesafe = 0;
      let enUzakNokta1 = new THREE.Vector3(); // Yüzey tarafı
      let enUzakNokta2 = new THREE.Vector3(); // Çizgi tarafı

      // Yüzey düzlem mi kontrol et
      const yuzeyTipi = yuzeyTipiTespit(yuzeyGeom);
      const yuzeyDuzlemMi = yuzeyTipi?.tip === 'duzlem';

      // Çizginin tüm noktalarından yüzeye en uzak olanı bul
      const cizgiPositionsEnUzak = cizgiGeom.attributes.position;
      for (let i = 0; i < cizgiPositionsEnUzak.count; i++) {
        const cizgiVertex = new THREE.Vector3(
          cizgiPositionsEnUzak.getX(i),
          cizgiPositionsEnUzak.getY(i),
          cizgiPositionsEnUzak.getZ(i)
        );

        // Bu noktadan yüzeye dik mesafe (yüzey düzlem ise)
        const dikMesafeTemp = Math.abs(yuzeyDuzlem.distanceToPoint(cizgiVertex));

        if (dikMesafeTemp > enUzakMesafe) {
          enUzakMesafe = dikMesafeTemp;
          enUzakNokta2 = cizgiVertex.clone();
          // Yüzey tarafı projeksiyon noktası
          const projNokta = new THREE.Vector3();
          yuzeyDuzlem.projectPoint(cizgiVertex, projNokta);
          enUzakNokta1 = projNokta;
        }
      }

      // 🔵 ÇEMBER/SİLİNDİR TESPİTİ - Merkez bilgisi
      const cizgiCember = this.cizgiCemberTespit(cizgiGeom);
      const yuzeySilindir = this.yuzeySilindirTespit(yuzeyGeom);

      // MESAFE GÖSTER - En kısa dik mesafe
      olcumGecmisineEkleCallback("Yüzey-Çizgi Mesafesi", dikMesafe, "mm", "Yüzey-Çizgi (En Kısa Dik)");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Yüzey-Çizgi Mesafesi");

      // 🎯 AÇI BİLGİSİ: Yüzey ile çizgi arasındaki açıyı da göster
      const aciDerece = cizgiYuzeyAcisi;

      // 🎯 PARALEL DEĞİL TESPİTİ: Çizgi yüzeye paralel değilse (açı > 0.001 derece)
      const yuzeyVeCizgiParalelDegil = aciDerece > 0.001;

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Yüzey-Çizgi Mesafesi (En Kısa Dik)",
          olcu: dikMesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: dikMesafe - tolerans,
          toleransMax: dikMesafe + tolerans,
          aci: aciDerece,
          // 🎯 PARALEL DEĞİL: Çizgi yüzeye paralel değilse uyarı flag'i
          paralelDegil: yuzeyVeCizgiParalelDegil,
          // 🔵 Merkez bilgileri
          cizgiMerkez: cizgiCember?.merkez,
          cizgiYaricap: cizgiCember?.yaricap,
          yuzeyMerkez: yuzeySilindir?.merkez,
          yuzeyYaricap: yuzeySilindir?.yaricap,
          yuzeyTip: yuzeySilindir?.tip
        }
      };

      // 🎯 ÖLÇÜM MODU: Ölçüm noktalarını belirle (Yüzey-Çizgi)
      // secim1 = Yüzey, secim2 = Çizgi
      // yuzeySilindir null ise yüzey düzlemdir
      const yuzey1IsDuzlem = !yuzeySilindir;
      let olcumNokta1 = dikProjeksiyonNokta.clone();
      let olcumNokta2 = enYakinCizgiNoktasi.clone();

      // Seçim 1 (Yüzey) için ölçüm modu kontrolü
      if (secim1OlcumModu === 'merkez' && secim1MerkezBilgisi?.merkez) {
        olcumNokta1 = secim1MerkezBilgisi.merkez.clone();
      } else if (secim1OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta1 = enUzakNokta1.clone();
      } else if (secim1OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim2MerkezBilgisi.merkez, secim1MerkezBilgisi.merkez).normalize();
          const yuzeyPos = yuzeyGeom.attributes.position;
          let enUzakProj = -Infinity;
          for (let i = 0; i < yuzeyPos.count; i++) {
            const vertex = new THREE.Vector3(yuzeyPos.getX(i), yuzeyPos.getY(i), yuzeyPos.getZ(i));
            const proj = vertex.clone().sub(secim1MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta1 = vertex.clone();
            }
          }
        } else {
          olcumNokta1 = enUzakNokta1.clone();
        }
      }
      // Seçim 2 (Çizgi) için ölçüm modu kontrolü
      if (secim2OlcumModu === 'merkez' && secim2MerkezBilgisi?.merkez) {
        olcumNokta2 = secim2MerkezBilgisi.merkez.clone();
      } else if (secim2OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta2 = enUzakNokta2.clone();
      } else if (secim2OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim1MerkezBilgisi.merkez, secim2MerkezBilgisi.merkez).normalize();
          const cizgiPos = cizgiGeom.attributes.position;
          let enUzakProj = -Infinity;
          for (let i = 0; i < cizgiPos.count; i++) {
            const vertex = new THREE.Vector3(cizgiPos.getX(i), cizgiPos.getY(i), cizgiPos.getZ(i));
            const proj = vertex.clone().sub(secim2MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta2 = vertex.clone();
            }
          }
        } else {
          olcumNokta2 = enUzakNokta2.clone();
        }
      }

      // 🎯 EN UZAK + DÜZLEM: Düzlem tarafında dik projeksiyon yap (CATIA style)
      if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak' || secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
        // Yüzey 1 düzlem ise, olcumNokta2'yi düzleme dik projekte et
        if (yuzey1IsDuzlem && yuzeyDuzlem && !secim1MerkezBilgisi) {
          const projNokta = new THREE.Vector3();
          yuzeyDuzlem.projectPoint(olcumNokta2, projNokta);
          olcumNokta1 = projNokta;
        }
      }

      // 🎯 ÖLÇÜM MODU: Mesafeyi yeniden hesapla
      const kisaDuzAktifYC = secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz';
      const modAktif = secim1OlcumModu !== 'enKisa' && secim1OlcumModu !== 'enKisaDuz' || secim2OlcumModu !== 'enKisa' && secim2OlcumModu !== 'enKisaDuz';
      let gosterilecekMesafe: number;

      // 🎯 KISA DÜZ: Her zaman dik (perpendicular) mesafe kullan - CATIA style
      if (kisaDuzAktifYC) {
        // Dik mesafe kullan
        if (yuzey1IsDuzlem && yuzeyDuzlem) {
          gosterilecekMesafe = Math.abs(yuzeyDuzlem.distanceToPoint(olcumNokta2));
        } else {
          gosterilecekMesafe = dikMesafe;
        }
      } else if (modAktif) {
        // En Uzak veya Merkez modunda: düzlem varsa dik mesafe, yoksa nokta-nokta mesafesi
        if (yuzey1IsDuzlem && yuzeyDuzlem && (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak')) {
          // Dik mesafe hesapla (CATIA style)
          gosterilecekMesafe = Math.abs(yuzeyDuzlem.distanceToPoint(olcumNokta2));
        } else {
          gosterilecekMesafe = olcumNokta1.distanceTo(olcumNokta2);
        }
      } else {
        gosterilecekMesafe = dikMesafe;
      }

      // Sonuç nesnesindeki mesafeyi güncelle
      if (sonuc?.mesafe) {
        sonuc.mesafe.olcu = gosterilecekMesafe;
        sonuc.mesafe.toleransMin = gosterilecekMesafe - tolerans;
        sonuc.mesafe.toleransMax = gosterilecekMesafe + tolerans;
        if (secim1OlcumModu === 'merkez' || secim2OlcumModu === 'merkez') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Merkeze');
        } else if (secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Kısa Düz');
        } else if (secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Uzak Düz');
        } else if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Uzak');
        } else if (secim1OlcumModu === 'enKisa' || secim2OlcumModu === 'enKisa') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Kısa');
        }
      }

      // Arrow line çiz - ÖLÇÜM MODU'NA GÖRE
      const mesafedonusmus = this.birimDonustur(gosterilecekMesafe, 'mm', secilenBirim, birimFaktore);

      // 🎯 PARALEL DEĞİL UYARISI: Çizgi yüzeye paralel değilse uyarı işareti ekle
      const paralelDegil = aciDerece > 0.001;
      const paralelUyarisi = paralelDegil ? ' ⚠∠' : '';

      let labelMetin = aciDerece > 0.1 ? `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim} (${aciDerece.toFixed(aciBasamak)}°)${paralelUyarisi}` : `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim}`;

      // 🎯 Ölçüm modu göstergesini label'a ekle
      if (secim1OlcumModu === 'enKisaDuz') labelMetin += ' [⊥1]';
      else if (secim1OlcumModu === 'enKisa') labelMetin += ' [↔1]';
      else if (secim1OlcumModu === 'merkez') labelMetin += ' [⊙1]';
      else if (secim1OlcumModu === 'enUzak') labelMetin += ' [⤢1]';
      else if (secim1OlcumModu === 'enUzakDuz') labelMetin += ' [⇕1]';
      if (secim2OlcumModu === 'enKisaDuz') labelMetin += ' [⊥2]';
      else if (secim2OlcumModu === 'enKisa') labelMetin += ' [↔2]';
      else if (secim2OlcumModu === 'merkez') labelMetin += ' [⊙2]';
      else if (secim2OlcumModu === 'enUzak') labelMetin += ' [⤢2]';
      else if (secim2OlcumModu === 'enUzakDuz') labelMetin += ' [⇕2]';

      // 🔵 Merkez bilgisini label'a ekle (en kısa modundayken)
      if (secim2OlcumModu === 'enKisa' && cizgiCember) {
        const r = this.birimDonustur(cizgiCember.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | Çizgi R:${r.toFixed(mesafeBas)}`;
      }
      if (secim1OlcumModu === 'enKisa' && yuzeySilindir) {
        const r = this.birimDonustur(yuzeySilindir.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | ${yuzeySilindir.tip === 'silindir' ? 'Silindir' : 'Küre'} R:${r.toFixed(mesafeBas)}`;
      }

      gosterimKayniyaEkleCallback(olcumNokta1, olcumNokta2, labelMetin);

      // 🔴 İKİNCİ ÇİZGİ KALDIRILDI: Merkeze ölçüm çizgisi artık çizilmiyor
      // Kullanıcı talebi: Sadece ana ölçüm çizgisi gösterilsin

      detectChangesCallback();
    }

    /** NOT: Çizgi - Yüzey Mesafesi */
    if (efektifTip1 == "Çizgi" && efektifTip2 == "Yüzey") {

      let cizgi: THREE.LineSegments = (secilen1?.altObje || secilen1?.object) as THREE.LineSegments;
      let yuzey: THREE.Mesh = (secilen2?.altObje || secilen2?.object) as THREE.Mesh;

      if (!cizgi || !yuzey) {
        return null;
      }

      // Yedeklenen geometrileri al ve CLONE'la
      // ÖNEMLI: Yüzey geometrisini fikoSecim2'den al (secilen2'in highlight geometrisi)
      let cizgiGeom = secilen1Geom?.clone();
      let yuzeyGeom = fikoSecim2.sonSecilenHighlightGeometriYuzey?.clone() || secilen2Geom?.clone();

      if (!cizgiGeom) {
        return null;
      }

      if (!yuzeyGeom) {
        return null;
      }

      // 🎯 DÜZELTME: ÇİZGİ GEOMETRİSİ ZATEN WORLD SPACE'DE!
      // secilen1Geom, cizgiGeometriAlVeTransformUygula() tarafından zaten dönüştürüldü
      // Tekrar transform UYGULANMAMALI - çift dönüşüm hatası!

      // Yüzey geometrisi - Highlight LOCAL space'de, transform uygula
      if (yuzey && yuzey.matrixWorld) {
        yuzeyGeom.applyMatrix4(yuzey.matrixWorld);
      }

      // İki geometri arasındaki en kısa mesafe (tüm geometri kontrol)
      // 🚀 GPU-accelerated hesaplama
      const mesafe = await this.geomService.enKisaMesafeHesapla(cizgiGeom, yuzeyGeom);

      // Yakın noktaları hesapla (gösterim için)
      let yakınNoktaYuzey = new THREE.Vector3();
      let yakınNoktaCizgiLocal = new THREE.Vector3();

      // Yüzey vertexlerinden en yakını bul
      const yuzeyPositions = yuzeyGeom.attributes.position.array as Float32Array;
      let minMesafe = Infinity;
      for (let i = 0; i < yuzeyPositions.length; i += 3) {
        const vertex = new THREE.Vector3(yuzeyPositions[i], yuzeyPositions[i + 1], yuzeyPositions[i + 2]);
        const dist = this.geomService.alCizgiNoktaMesafe(cizgiGeom, vertex);
        if (dist < minMesafe) {
          minMesafe = dist;
          yakınNoktaYuzey = vertex.clone();
          const projeNokta = this.geomService.alCizgiProjeNokta(cizgiGeom, vertex);
          yakınNoktaCizgiLocal = projeNokta.nokta.clone();
        }
      }

      // Çizginin yön vektörünü hesapla (transform'lanmış geometriden)
      let cizgiStart = new THREE.Vector3();
      let cizgiEnd = new THREE.Vector3();
      const cizgiPos = cizgiGeom.attributes.position;

      if (cizgiPos.count >= 2) {
        cizgiStart = new THREE.Vector3(cizgiPos.getX(0), cizgiPos.getY(0), cizgiPos.getZ(0));
        cizgiEnd = new THREE.Vector3(cizgiPos.getX(cizgiPos.count - 1), cizgiPos.getY(cizgiPos.count - 1), cizgiPos.getZ(cizgiPos.count - 1));
      }
      const cizgiDir = new THREE.Vector3().subVectors(cizgiEnd, cizgiStart).normalize();

      // Yüzeyin normalını al
      const yuzeyNormal = this.geomService.alYuzeyNormal(yuzeyGeom);

      // Çizgi yönü ile yüzey normalı arasındaki açıyı hesapla
      const dotProduct = Math.abs(cizgiDir.dot(yuzeyNormal));
      const radiyan = Math.acos(Math.min(1, dotProduct));
      const normalArasiAci = (radiyan * 180) / Math.PI;

      // Çizgi-Yüzey açısı: Çizgi yüzeyde ne kadar açılı
      // Normal ile çizgi arası 90° ise çizgi yüzeyde paralel (açı = 0)
      // Normal ile çizgi arası 0° ise çizgi yüzeyde dik (açı = 90)
      const cizgiYuzeyAcisi = Math.abs(90 - normalArasiAci);

      // 🎯 HER ZAMAN DİK MESAFE ÖLÇÜMÜ - EN KISA DİK MESAFE (düzlem seçildiğinde)
      // Çizginin tüm noktalarından yüzey düzlemine dik mesafe hesapla, en kısa olanı bul

      // Yüzey düzlemini oluştur (tıklanan noktadan geçen)
      const yuzeyDuzlem = new THREE.Plane();
      const yuzeyTiklamaNokta = secilen2.point.clone();
      yuzeyDuzlem.setFromNormalAndCoplanarPoint(yuzeyNormal, yuzeyTiklamaNokta);

      // Çizginin tüm noktalarından yüzeye dik mesafe hesapla - 🚀 GPU ACCELERATED
      let enKisaDikMesafe = Infinity;
      let enYakinCizgiNoktasi = new THREE.Vector3();
      let enYakinDikProjeksiyonNoktasi = new THREE.Vector3();

      try {
        // GPU ile hesapla
        const gpuResult = await this.geomService.cizgiDuzlemMesafeHesapla(cizgiGeom, yuzeyNormal, yuzeyTiklamaNokta);
        enKisaDikMesafe = gpuResult.mesafe;
        enYakinCizgiNoktasi = gpuResult.enYakinNokta;
        enYakinDikProjeksiyonNoktasi = gpuResult.projeksiyon;
      } catch (e) {
        // CPU Fallback
        for (let i = 0; i < cizgiPos.count; i++) {
          const cizgiVertex = new THREE.Vector3(
            cizgiPos.getX(i),
            cizgiPos.getY(i),
            cizgiPos.getZ(i)
          );

          // Bu noktadan yüzeye dik mesafe
          const dikMesafeTemp = Math.abs(yuzeyDuzlem.distanceToPoint(cizgiVertex));

          if (dikMesafeTemp < enKisaDikMesafe) {
            enKisaDikMesafe = dikMesafeTemp;
            enYakinCizgiNoktasi = cizgiVertex.clone();
            // Projeksiyon noktası
            yuzeyDuzlem.projectPoint(cizgiVertex, enYakinDikProjeksiyonNoktasi);
          }
        }
      }

      const dikMesafe = enKisaDikMesafe;
      const dikProjeksiyonNokta = enYakinDikProjeksiyonNoktasi;

      // 🎯 EN UZAK MESAFE: Geometriden gerçek en uzak noktaları bul (CATIA style)
      let enUzakMesafe = 0;
      let enUzakNokta1 = new THREE.Vector3(); // Çizgi tarafı
      let enUzakNokta2 = new THREE.Vector3(); // Yüzey tarafı

      // Çizginin tüm noktalarından yüzeye en uzak olanı bul
      for (let i = 0; i < cizgiPos.count; i++) {
        const cizgiVertex = new THREE.Vector3(
          cizgiPos.getX(i),
          cizgiPos.getY(i),
          cizgiPos.getZ(i)
        );

        // Bu noktadan yüzeye dik mesafe
        const dikMesafeTemp = Math.abs(yuzeyDuzlem.distanceToPoint(cizgiVertex));

        if (dikMesafeTemp > enUzakMesafe) {
          enUzakMesafe = dikMesafeTemp;
          enUzakNokta1 = cizgiVertex.clone();
          // Yüzey tarafı projeksiyon noktası
          const projNokta = new THREE.Vector3();
          yuzeyDuzlem.projectPoint(cizgiVertex, projNokta);
          enUzakNokta2 = projNokta;
        }
      }

      // 🔵 ÇEMBER/SİLİNDİR TESPİTİ - Merkez bilgisi
      const cizgiCember = this.cizgiCemberTespit(cizgiGeom);
      const yuzeySilindir = this.yuzeySilindirTespit(yuzeyGeom);

      // MESAFE GÖSTER - En kısa dik mesafe
      olcumGecmisineEkleCallback("Çizgi-Yüzey Mesafesi", dikMesafe, "mm", "Çizgi-Yüzey (En Kısa Dik)");

      // Min/Max/Ortalama hesapla
      const minMaxOrta = this.geomService.hesaplaMinMaxOrtalama(olcumGecmisi, "Çizgi-Yüzey Mesafesi");

      // 🎯 AÇI BİLGİSİ: Yüzey ile çizgi arasındaki açıyı da göster
      const aciDerece = cizgiYuzeyAcisi;

      // 🎯 PARALEL DEĞİL TESPİTİ: Çizgi yüzeye paralel değilse (açı > 0.001 derece)
      const cizgiYuzeyeParalelDegil = aciDerece > 0.001;

      sonuc = {
        turu: 'Mesafe',
        mesafe: {
          aciklama: "Çizgi-Yüzey Mesafesi (En Kısa Dik)",
          olcu: dikMesafe,
          birim: "mm",
          min: minMaxOrta?.min,
          max: minMaxOrta?.max,
          ortalama: minMaxOrta?.ortalama,
          toleransMin: dikMesafe - tolerans,
          toleransMax: dikMesafe + tolerans,
          aci: aciDerece,
          // 🎯 PARALEL DEĞİL: Çizgi yüzeye paralel değilse uyarı flag'i
          paralelDegil: cizgiYuzeyeParalelDegil,
          // 🔵 Merkez bilgileri
          cizgiMerkez: cizgiCember?.merkez,
          cizgiYaricap: cizgiCember?.yaricap,
          yuzeyMerkez: yuzeySilindir?.merkez,
          yuzeyYaricap: yuzeySilindir?.yaricap,
          yuzeyTip: yuzeySilindir?.tip
        }
      };

      // 🎯 ÖLÇÜM MODU: Ölçüm noktalarını belirle (Çizgi-Yüzey)
      // secim1 = Çizgi, secim2 = Yüzey
      let olcumNokta1 = enYakinCizgiNoktasi.clone();
      let olcumNokta2 = dikProjeksiyonNokta.clone();

      // Seçim 1 (Çizgi) için ölçüm modu kontrolü
      if (secim1OlcumModu === 'merkez' && secim1MerkezBilgisi?.merkez) {
        olcumNokta1 = secim1MerkezBilgisi.merkez.clone();
      } else if (secim1OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta1 = enUzakNokta1.clone();
      } else if (secim1OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim2MerkezBilgisi.merkez, secim1MerkezBilgisi.merkez).normalize();
          let enUzakProj = -Infinity;
          for (let i = 0; i < cizgiPos.count; i++) {
            const vertex = new THREE.Vector3(cizgiPos.getX(i), cizgiPos.getY(i), cizgiPos.getZ(i));
            const proj = vertex.clone().sub(secim1MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta1 = vertex.clone();
            }
          }
        } else {
          olcumNokta1 = enUzakNokta1.clone();
        }
      }
      // Seçim 2 (Yüzey) için ölçüm modu kontrolü
      if (secim2OlcumModu === 'merkez' && secim2MerkezBilgisi?.merkez) {
        olcumNokta2 = secim2MerkezBilgisi.merkez.clone();
      } else if (secim2OlcumModu === 'enUzak') {
        // 🎯 EN UZAK - GEOMETRİDEN BULUNAN GERÇEK EN UZAK NOKTA (CATIA style)
        olcumNokta2 = enUzakNokta2.clone();
      } else if (secim2OlcumModu === 'enUzakDuz') {
        // 🎯 EN UZAK DÜZ - Aynı yöndeki en uzak nokta (çapraz değil)
        if (secim1MerkezBilgisi?.merkez && secim2MerkezBilgisi?.merkez) {
          const merkezYon = new THREE.Vector3().subVectors(secim1MerkezBilgisi.merkez, secim2MerkezBilgisi.merkez).normalize();
          const yuzeyPos = yuzeyGeom.attributes.position;
          let enUzakProj = -Infinity;
          for (let i = 0; i < yuzeyPos.count; i++) {
            const vertex = new THREE.Vector3(yuzeyPos.getX(i), yuzeyPos.getY(i), yuzeyPos.getZ(i));
            const proj = vertex.clone().sub(secim2MerkezBilgisi.merkez).dot(merkezYon);
            if (proj > enUzakProj) {
              enUzakProj = proj;
              olcumNokta2 = vertex.clone();
            }
          }
        } else {
          olcumNokta2 = enUzakNokta2.clone();
        }
      }

      // 🎯 ÖLÇÜM MODU: Mesafeyi yeniden hesapla
      const kisaDuzAktifCY = secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz';
      const modAktif = secim1OlcumModu !== 'enKisa' && secim1OlcumModu !== 'enKisaDuz' || secim2OlcumModu !== 'enKisa' && secim2OlcumModu !== 'enKisaDuz';
      let gosterilecekMesafe: number;

      // 🎯 KISA DÜZ: Her zaman dik (perpendicular) mesafe kullan - CATIA style
      if (kisaDuzAktifCY) {
        gosterilecekMesafe = dikMesafe;
      } else if (modAktif) {
        gosterilecekMesafe = olcumNokta1.distanceTo(olcumNokta2);
      } else {
        gosterilecekMesafe = dikMesafe;
      }

      // Sonuç nesnesindeki mesafeyi güncelle
      if (sonuc?.mesafe) {
        sonuc.mesafe.olcu = gosterilecekMesafe;
        sonuc.mesafe.toleransMin = gosterilecekMesafe - tolerans;
        sonuc.mesafe.toleransMax = gosterilecekMesafe + tolerans;
        if (secim1OlcumModu === 'merkez' || secim2OlcumModu === 'merkez') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Merkeze');
        } else if (secim1OlcumModu === 'enKisaDuz' || secim2OlcumModu === 'enKisaDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Kısa Düz');
        } else if (secim1OlcumModu === 'enUzakDuz' || secim2OlcumModu === 'enUzakDuz') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'Uzak Düz');
        } else if (secim1OlcumModu === 'enUzak' || secim2OlcumModu === 'enUzak') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Uzak');
        } else if (secim1OlcumModu === 'enKisa' || secim2OlcumModu === 'enKisa') {
          sonuc.mesafe.aciklama = sonuc.mesafe.aciklama.replace('En Kısa Dik', 'En Kısa');
        }
      }

      // Arrow line çiz - ÖLÇÜM MODU'NA GÖRE
      const mesafedonusmus = this.birimDonustur(gosterilecekMesafe, 'mm', secilenBirim, birimFaktore);

      // 🎯 PARALEL DEĞİL UYARISI: Çizgi yüzeye paralel değilse uyarı işareti ekle
      const paralelDegil = aciDerece > 0.001;
      const paralelUyarisi = paralelDegil ? ' ⚠∠' : '';

      let labelMetin = aciDerece > 0.1 ? `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim} (${aciDerece.toFixed(aciBasamak)}°)${paralelUyarisi}` : `${mesafedonusmus.toFixed(mesafeBas)} ${secilenBirim}`;

      // 🎯 Ölçüm modu göstergesini label'a ekle
      if (secim1OlcumModu === 'enKisaDuz') labelMetin += ' [⊥1]';
      else if (secim1OlcumModu === 'enKisa') labelMetin += ' [↔1]';
      else if (secim1OlcumModu === 'merkez') labelMetin += ' [⊙1]';
      else if (secim1OlcumModu === 'enUzak') labelMetin += ' [⤢1]';
      else if (secim1OlcumModu === 'enUzakDuz') labelMetin += ' [⇕1]';
      if (secim2OlcumModu === 'enKisaDuz') labelMetin += ' [⊥2]';
      else if (secim2OlcumModu === 'enKisa') labelMetin += ' [↔2]';
      else if (secim2OlcumModu === 'merkez') labelMetin += ' [⊙2]';
      else if (secim2OlcumModu === 'enUzak') labelMetin += ' [⤢2]';
      else if (secim2OlcumModu === 'enUzakDuz') labelMetin += ' [⇕2]';

      // 🔵 Merkez bilgisini label'a ekle (en kısa modundayken)
      if (secim1OlcumModu === 'enKisa' && cizgiCember) {
        const r = this.birimDonustur(cizgiCember.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | Çizgi R:${r.toFixed(mesafeBas)}`;
      }
      if (secim2OlcumModu === 'enKisa' && yuzeySilindir) {
        const r = this.birimDonustur(yuzeySilindir.yaricap, 'mm', secilenBirim, birimFaktore);
        labelMetin += ` | ${yuzeySilindir.tip === 'silindir' ? 'Silindir' : 'Küre'} R:${r.toFixed(mesafeBas)}`;
      }

      gosterimKayniyaEkleCallback(olcumNokta1, olcumNokta2, labelMetin);

      // 🔴 İKİNCİ ÇİZGİ KALDIRILDI: Merkeze ölçüm çizgisi artık çizilmiyor
      // Kullanıcı talebi: Sadece ana ölçüm çizgisi gösterilsin

      detectChangesCallback();
    }

    // Eğer hiçbir kombinasyon match olmadıysa, dummy sonuc ata
    if (!sonuc) {
      sonuc = { turu: 'Hata', mesafe: { aciklama: "Desteklenmeyen kombinasyon", olcu: 0, birim: "mm" } };
      detectChangesCallback();
    }

    return sonuc;
  }
}
